import './FinancialSuite.css';
import React, { useState, useEffect } from 'react';
import Cookies from 'universal-cookie';
import ObexRequestsHandlerP80 from '../../../../handlers/ObexRequestsHandlerP80';
import { getActiveCompanyId } from '../../../../services/apiUtils';
import { listJournalLinesByEntry } from '../../../../services/JournalEntriesService';
import { Skeleton } from '../../../../components/shared/Skeleton/Skeleton';
import Modal from '../../../../components/shared/Modal/Modal';
import {
  BookOpen, FileText, Receipt, ShieldCheck, Scale, Coins,
  ArrowRightLeft, Landmark, FileSpreadsheet, Plus, Trash2,
  Edit3, Check, Save, Calculator, HelpCircle, RefreshCw,
  FolderOpen, ShieldAlert, FileOutput, Key, Briefcase, FileInput, CheckCircle2,
  History, Percent, Mail
} from 'lucide-react';

import { JournalLine, JournalEntry } from '../../../../types/Finances.types';
// Activos fijos ahora usa views/FixedAssetsView desde FinancesView; se deja por si hay que volver a esta
// import { FixedAssetsView } from './FixedAssetsView';

import { DepreciationMethod, DEPRECIATION_METHOD_TYPE_LABELS } from '../../../../types/DepreciationMethod.types';
import { listDepreciationMethods, createDepreciationMethod } from '../../../../services/DepreciationMethodsService';
import FinancePostingPeriodsView from './masters/FinancePostingPeriodsView';

const cn = (...args: any[]) => args.filter(Boolean).join(' ');



const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  issued: 'Emitida',
  executive: 'Vía ejecutiva',
  collected: 'Cobrada',
  collected_negotiated: 'Cobrada (negociada)',
  void: 'Anulada',
  voided: 'Anulada',
  cancelled: 'Cancelada'
};

// journal_entries_get se pide una vez por asiento: se limita la concurrencia y se
// reutiliza el resultado unos segundos para que reabrir la pantalla no repita todo.
const JOURNAL_DETAIL_CONCURRENCY = 6;
const JOURNAL_CACHE_MS = 30000;
let journalCache: { companyId: string; at: number; entries: JournalEntry[] } | null = null;

const mapWithLimit = async <T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
};

// Pantallas que no usan los asientos: no hace falta cargarlos para mostrarlas
const MENUS_WITHOUT_JOURNAL = ['facturas_historial', 'depreciation_methods'];

interface FinancialSuiteProps {
  privacyMode: boolean;
  activeMenu?: string;
  onChangeMenu?: (menuId: string) => void;
  hideSidebar?: boolean;
  headerTitle?: string;
  headerDesc?: string;
  // Solo ese modelo en la pantalla de impuestos; sin él se ven los dos
  taxModel?: '303' | '111';
}

export default function FinancialSuite({
  privacyMode,
  activeMenu: controlledMenu,
  onChangeMenu,
  hideSidebar = false,
  headerTitle,
  headerDesc,
  taxModel
}: FinancialSuiteProps) {
  const cookies = new Cookies();
  const getAccessToken = () => {
    const userCookie = cookies.get('101Obex');
    return userCookie?.token || '';
  };

  // State for Journal Entries - initialized from live backend or empty
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [deleteModalItem, setDeleteModalItem] = useState<string | null>(null);

  // Chart of Accounts (PGC) - populated dynamically from backend
  const [chartOfAccounts, setChartOfAccounts] = useState<{ code: string; name: string; id?: string; level?: string }[]>([]);
  // Cuentas contra las que se puede asentar: no grupos ni subgrupos del PGC.
  const postableAccounts = chartOfAccounts.filter(c => c.level !== 'group' && c.level !== 'subgroup');

  // Bank Movements for reconciliation - populated dynamically from backend
  const [unreconciledMovements, setUnreconciledMovements] = useState<any[]>([]);
  const [journalLoading, setJournalLoading] = useState(true);
  const [bankLoading, setBankLoading] = useState(true);

  // Conciliación: facturas abiertas contra las que se puede cuadrar un ingreso,
  // la factura/cuenta elegida por movimiento y los movimientos cuyo asiento ya
  // se contabilizó pero todavía no se pudo marcar como conciliado en el backend
  // (se reintenta solo el marcado, nunca se duplica el asiento).
  const [bankOpenInvoices, setBankOpenInvoices] = useState<any[]>([]);
  const [bankMatch, setBankMatch] = useState<Record<string, string>>({});
  const [bankCounterAccount, setBankCounterAccount] = useState<Record<string, string>>({});
  const [bankPosted, setBankPosted] = useState<Record<string, boolean>>({});
  const [bankBusyId, setBankBusyId] = useState<string | null>(null);
  // Propuesta automática por movimiento (factura sugerida, puntuación y motivos).
  // Es solo de sesión: el backend no tiene dónde guardar un estado "pendiente de validar".
  const [bankProposals, setBankProposals] = useState<Record<string, { invoiceId: string; score: number; reasons: string[] }>>({});
  const [bankBatchRunning, setBankBatchRunning] = useState(false);

  // Real clients, needed so "Factura Emitida" posts a client_id that actually
  // exists (client_id=1 does not exist in the DB — it was previously hardcoded
  // and every invoice created through this form silently failed on the FK).
  const [clientsList, setClientsList] = useState<{ id: string; name: string; cif: string }[]>([]);

  // Fetch Clients from backend
  const fetchClientsList = async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      let res = await ObexRequestsHandlerP80.get(`/client-list?access_token=${encodeURIComponent(token)}&page=1&page_size=100`);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      const list = res.response?.data || res.response || res.data || res;
      if (Array.isArray(list)) {
        setClientsList(list.map((c: any) => ({ id: String(c.id), name: c.name || 'Cliente', cif: c.cif || '' })));
      }
    } catch (e) {
      console.error('Error fetching clients:', e);
    }
  };

  // Invoice history (Historial de Facturas): list + detail, both from real backend endpoints
  const [invoicesHistory, setInvoicesHistory] = useState<any[]>([]);
  const [invoicesHistoryLoading, setInvoicesHistoryLoading] = useState(false);
  const [invoicesHistoryUnavailable, setInvoicesHistoryUnavailable] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [selectedInvoiceDetail, setSelectedInvoiceDetail] = useState<any | null>(null);
  const [invoiceEmailStatus, setInvoiceEmailStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);

  const fetchInvoicesHistory = async () => {
    const token = getAccessToken();
    if (!token) return;
    setInvoicesHistoryLoading(true);
    try {
      let res = await ObexRequestsHandlerP80.get(`/invoices_list?access_token=${encodeURIComponent(token)}&company_id=${encodeURIComponent(getActiveCompanyId())}`);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      const list = res.response?.data || res.response || res.data || res;
      if (Array.isArray(list)) {
        setInvoicesHistory(list);
        setInvoicesHistoryUnavailable(false);
      } else {
        setInvoicesHistory([]);
        setInvoicesHistoryUnavailable(true);
      }
    } catch (e) {
      console.error('Error fetching invoices history:', e);
      setInvoicesHistory([]);
      setInvoicesHistoryUnavailable(true);
    } finally {
      setInvoicesHistoryLoading(false);
    }
  };

  const fetchInvoiceDetail = async (invoiceId: string) => {
    const token = getAccessToken();
    if (!token) return;
    setSelectedInvoiceId(invoiceId);
    setInvoiceDetailLoading(true);
    try {
      let res = await ObexRequestsHandlerP80.get(`/invoices_get?access_token=${encodeURIComponent(token)}&id=${encodeURIComponent(invoiceId)}`);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      const detail = res.response || res.data || res;
      setSelectedInvoiceDetail(detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : null);
    } catch (e) {
      console.error('Error fetching invoice detail:', e);
      setSelectedInvoiceDetail(null);
    } finally {
      setInvoiceDetailLoading(false);
    }
  };

  // Invoice lifecycle actions (issue / collect / set_executive / void / negotiate)
  const [invoiceActionLoading, setInvoiceActionLoading] = useState<string | null>(null);
  const [voidModalInvoice, setVoidModalInvoice] = useState<{ id: string; number: string } | null>(null);
  const [negotiateModalInvoice, setNegotiateModalInvoice] = useState<{ id: string; number: string; total: number } | null>(null);
  const [negotiateAmount, setNegotiateAmount] = useState('');
  const [negotiateTerms, setNegotiateTerms] = useState('');

  const runInvoiceAction = async (invoiceId: string, path: string, successMsg: string) => {
    const token = getAccessToken();
    if (!token) return;
    setInvoiceActionLoading(invoiceId);
    try {
      let res = await ObexRequestsHandlerP80.get(`${path}${path.includes('?') ? '&' : '?'}access_token=${encodeURIComponent(token)}`);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      showFeedback(successMsg);
      await fetchInvoicesHistory();
      if (selectedInvoiceId === invoiceId) await fetchInvoiceDetail(invoiceId);
    } catch (e) {
      console.error('Error running invoice action:', e);
      showFeedback('No se pudo completar la acción sobre la factura.', true);
    } finally {
      setInvoiceActionLoading(null);
    }
  };

  const handleIssueInvoice = (id: string) => runInvoiceAction(id, `/invoices_issue?id=${encodeURIComponent(id)}`, `Factura #${id} emitida correctamente.`);
  const handleCollectInvoice = (id: string) => runInvoiceAction(id, `/invoices_collect?id=${encodeURIComponent(id)}`, `Factura #${id} marcada como cobrada.`);
  const handleSetExecutiveInvoice = (id: string) => runInvoiceAction(id, `/invoices_set_executive?id=${encodeURIComponent(id)}`, `Factura #${id} pasada a vía ejecutiva.`);

  const confirmVoidInvoice = async () => {
    if (!voidModalInvoice) return;
    const { id, number } = voidModalInvoice;
    setVoidModalInvoice(null);
    await runInvoiceAction(id, `/invoices_void?id=${encodeURIComponent(id)}`, `Factura ${number} anulada.`);
    if (selectedInvoiceId === id) { setSelectedInvoiceId(null); setSelectedInvoiceDetail(null); }
  };

  const confirmNegotiateInvoice = async () => {
    if (!negotiateModalInvoice) return;
    const amount = parseFloat(negotiateAmount);
    if (isNaN(amount) || amount < 0) {
      showFeedback('Introduce un importe negociado válido.', true);
      return;
    }
    const { id, number } = negotiateModalInvoice;
    setNegotiateModalInvoice(null);
    await runInvoiceAction(
      id,
      `/invoices_negotiate?id=${encodeURIComponent(id)}&negotiated_amount=${encodeURIComponent(amount.toFixed(2))}&terms=${encodeURIComponent(negotiateTerms || 'Acuerdo de negociación')}`,
      `Factura ${number} renegociada a ${formatCurrency(amount)}.`
    );
  };

  // Send invoice by email
  const handleSendInvoiceEmail = async (invoiceId: string, invoiceDetail: any) => {
    const token = getAccessToken();
    if (!token) return;
    
    // Determinar email destino: cliente o pedir al usuario
    let toEmail = invoiceDetail?.client_email;
    if (!toEmail) {
      toEmail = window.prompt('El cliente no tiene email configurado. Introduce el email destino:');
      if (!toEmail || !toEmail.trim()) {
        showFeedback('Email requerido para enviar la factura.', true);
        return;
      }
      toEmail = toEmail.trim();
    }
    
    setInvoiceEmailStatus('sending');
    try {
      // Generar HTML del email
      // const { InvoiceEmailTemplate } = require('../components/InvoiceEmailTemplate.tsx');
      const formatCurrency = (n: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(n);
      
      const lines = invoiceDetail.lines || [];
      const client = invoiceDetail.client || {};
      
      // Crear objeto invoice para el template
      const invoiceForTemplate = {
        invoiceId: invoiceDetail.id,
        invoiceNumber: invoiceDetail.invoice_number || invoiceDetail.number,
        date: invoiceDetail.date,
        client: client,
        lines: lines.map((line: any) => ({
          name: line.description,
          quantity: line.quantity,
          price: parseFloat(line.unit_price) || 0,
          vat: line.tax_rate || line.vat || 0,
          base: (parseFloat(line.unit_price) || 0) * line.quantity,
          vatVal: (parseFloat(line.unit_price) || 0) * line.quantity * (line.tax_rate || line.vat || 0) / 100,
          sku: line.sku
        })),
        subtotal: invoiceDetail.subtotal || 0,
        vatAmount: invoiceDetail.vat_amount || invoiceDetail.vatAmount || 0,
        totalFactura: invoiceDetail.total || invoiceDetail.totalFactura || 0,
        vatBreakdown: invoiceDetail.vatBreakdown || {},
        paymentTerms: invoiceDetail.payment_terms || invoiceDetail.paymentTerms,
        bankAccount: invoiceDetail.bank_account_id || invoiceDetail.bankAccount,
      };
      
      // Generar HTML usando ReactDOMServer
      const ReactDOMServer = require('react-dom/server');
      const { InvoiceEmailTemplate } = require('./components/InvoiceEmailTemplate');
      
      const templateElement = React.createElement(InvoiceEmailTemplate.default || InvoiceEmailTemplate, {
        invoice: invoiceForTemplate,
        companyName: 'Marcial Pons',
        companyAddress: '',
        companyCif: '',
        companyEmail: '',
        companyPhone: '',
      });
      
      const htmlContent = ReactDOMServer.renderToStaticMarkup(templateElement);
      const fullHtml = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Factura ${invoiceDetail.invoice_number || invoiceDetail.id}</title>
</head>
<body style="margin: 0; padding: 0;">${ReactDOMServer.renderToStaticMarkup(React.createElement(InvoiceEmailTemplate.default || InvoiceEmailTemplate, {
        invoice: {
          invoiceId: invoiceDetail.id,
          invoiceNumber: invoiceDetail.invoice_number || invoiceDetail.number,
          date: invoiceDetail.date,
          client: invoiceDetail.client || {},
          lines: (invoiceDetail.lines || []).map((line: any) => ({
            name: line.description,
            quantity: line.quantity,
            price: parseFloat(line.unit_price) || 0,
            vat: line.tax_rate || line.vat || 0,
            base: (parseFloat(line.unit_price) || 0) * line.quantity,
            vatVal: (parseFloat(line.unit_price) || 0) * line.quantity * (line.tax_rate || line.vat || 0) / 100,
            sku: line.sku
          })),
          subtotal: invoiceDetail.subtotal || 0,
          vatAmount: invoiceDetail.vat_amount || invoiceDetail.vatAmount || 0,
          totalFactura: invoiceDetail.total || invoiceDetail.totalFactura || 0,
          vatBreakdown: invoiceDetail.vatBreakdown || {},
          paymentTerms: invoiceDetail.payment_terms || invoiceDetail.paymentTerms,
          bankAccount: invoiceDetail.bank_account_id || invoiceDetail.bankAccount,
        },
        companyName: 'Marcial Pons',
        companyAddress: '',
        companyCif: '',
        companyEmail: '',
        companyPhone: '',
      }))}
</body>
</html>`;

      // Preparar parámetros para /send_mail
      const token = getAccessToken();
      // toEmail already determined above
      const subject = `Factura ${invoiceDetail.invoice_number || invoiceDetail.id} - Marcial Pons`;
      
      const params = new URLSearchParams({
        access_token: token,
        to: toEmail,
        subject: `Factura ${invoiceDetail.invoice_number || invoiceDetail.id} - Marcial Pons`,
        content: fullHtml,
      });
      
      const url = `/send_mail?${params.toString()}`;
      
      const response = await ObexRequestsHandlerP80.get(url);
      
      let resData = response;
      if (response?.data) resData = response.data;
      if (resData?.result) resData = resData.result;
      
      setInvoiceEmailStatus('success');
      showFeedback('Factura enviada correctamente por email');
      
      // Resetear estado después de 3 segundos
      setTimeout(() => setInvoiceEmailStatus('idle'), 3000);
      
    } catch (e) {
      console.error('Error sending invoice email:', (e as any).message || e);
      setInvoiceEmailStatus('error');
      showFeedback(`Error al enviar email: ${(e as any)?.message || 'Error desconocido'}`, true);
      
      // Resetear estado después de 3 segundos
      setTimeout(() => setInvoiceEmailStatus('idle'), 3000);
    }
  };

  // Edit invoice header (only valid while status = draft, per invoices_update's own rule)
  const [editInvoiceModal, setEditInvoiceModal] = useState<{ id: string; number: string } | null>(null);
  const [editInvoiceForm, setEditInvoiceForm] = useState({
    series: '', invoiceNumber: '', dueDate: '', paymentMethod: '', bankAccountId: '', currency: 'EUR', paymentInstrumentRef: ''
  });

  const openEditInvoice = (inv: any) => {
    setEditInvoiceModal({ id: String(inv.id), number: inv.invoice_number || `#${inv.id}` });
    setEditInvoiceForm({
      series: inv.series && inv.series !== 'None' ? inv.series : '',
      invoiceNumber: inv.invoice_number || '',
      dueDate: inv.due_date || '',
      paymentMethod: inv.payment_method || '',
      bankAccountId: inv.bank_account_id != null ? String(inv.bank_account_id) : '',
      currency: inv.currency || 'EUR',
      paymentInstrumentRef: inv.payment_instrument_ref || ''
    });
  };

  const confirmEditInvoice = async () => {
    if (!editInvoiceModal) return;
    const { id, number } = editInvoiceModal;
    const f = editInvoiceForm;
    setEditInvoiceModal(null);
    let url = `/invoices_update?id=${encodeURIComponent(id)}`;
    if (f.series) url += `&series=${encodeURIComponent(f.series)}`;
    if (f.invoiceNumber) url += `&invoice_number=${encodeURIComponent(f.invoiceNumber)}`;
    if (f.dueDate) url += `&due_date=${encodeURIComponent(f.dueDate)}`;
    if (f.paymentMethod) url += `&payment_method=${encodeURIComponent(f.paymentMethod)}`;
    if (f.bankAccountId) url += `&bank_account_id=${encodeURIComponent(f.bankAccountId)}`;
    if (f.currency) url += `&currency=${encodeURIComponent(f.currency)}`;
    if (f.paymentInstrumentRef) url += `&payment_instrument_ref=${encodeURIComponent(f.paymentInstrumentRef)}`;
    await runInvoiceAction(id, url, `Factura ${number} actualizada correctamente.`);
  };

  // Invoice line editing (invoice_lines_update / invoice_lines_remove). Both
  // endpoints reject lines of invoices that are no longer draft, so the buttons
  // below are gated on exactly the same condition the backend enforces.
  const [editLineModal, setEditLineModal] = useState<{ invoiceId: string; lineId: string; invoiceNumber: string } | null>(null);
  const [editLineForm, setEditLineForm] = useState({ description: '', quantity: '1', unitPrice: '0', discountPct: '0', taxRate: '21' });
  const [removeLineModal, setRemoveLineModal] = useState<{ invoiceId: string; lineId: string; description: string } | null>(null);

  const openEditLine = (invoiceId: string, invoiceNumber: string, line: any) => {
    setEditLineModal({ invoiceId, lineId: String(line.id), invoiceNumber });
    setEditLineForm({
      description: line.description || '',
      quantity: String(line.quantity ?? 1),
      unitPrice: String(line.unit_price ?? 0),
      // Not editable: no creation form exposes a discount, so this modal mirrors
      // the same fields those forms offer. Kept in state so it round-trips
      // untouched and line_total stays correct for a line that does carry one.
      discountPct: String(line.discount_pct ?? 0),
      taxRate: String(line.tax_rate ?? 21)
    });
  };

  const confirmEditLine = async () => {
    if (!editLineModal) return;
    const { invoiceId, lineId, invoiceNumber } = editLineModal;
    const f = editLineForm;
    const quantity = parseFloat(f.quantity);
    const unitPrice = parseFloat(f.unitPrice);
    const discountPct = parseFloat(f.discountPct) || 0;
    const taxRate = parseFloat(f.taxRate) || 0;
    if (!f.description.trim() || isNaN(quantity) || quantity <= 0 || isNaN(unitPrice) || unitPrice < 0) {
      showFeedback('Revise la línea: descripción obligatoria, cantidad mayor que cero e importe no negativo.', true);
      return;
    }
    // invoice_lines_update interpolates the description straight into its SQL,
    // so a straight apostrophe breaks the query. Swapped for a typographic one
    // until the backend escapes it properly.
    const description = f.description.trim().replace(/'/g, '\u2019');
    // line_total is stored net of VAT (same convention both creation flows use).
    // It has to be recomputed here: invoice_lines_update stores whatever it is
    // given, so leaving it out would keep the old amount on screen.
    const lineTotal = (quantity * unitPrice * (1 - discountPct / 100)).toFixed(2);
    setEditLineModal(null);
    const url = `/invoice_lines_update?id=${encodeURIComponent(lineId)}`
      + `&description=${encodeURIComponent(description)}`
      + `&quantity=${encodeURIComponent(String(quantity))}`
      + `&unit_price=${encodeURIComponent(unitPrice.toFixed(2))}`
      + `&discount_pct=${encodeURIComponent(String(discountPct))}`
      + `&tax_rate=${encodeURIComponent(String(taxRate))}`
      + `&line_total=${encodeURIComponent(lineTotal)}`;
    await runInvoiceAction(invoiceId, url, `Línea actualizada en la factura ${invoiceNumber}.`);
  };

  const confirmRemoveLine = async () => {
    if (!removeLineModal) return;
    const { invoiceId, lineId } = removeLineModal;
    setRemoveLineModal(null);
    await runInvoiceAction(invoiceId, `/invoice_lines_remove?id=${encodeURIComponent(lineId)}`, 'Línea eliminada de la factura.');
  };

  // Fetch Journal Entries from backend. Takes the already-loaded chart of
  // accounts because journal_lines only stores account_id: the real account
  // code has to be resolved here against chart_of_accounts.
  const fetchJournalEntries = async (pgc: { code: string; name: string; id?: string }[] = []) => {
    const token = getAccessToken();
    if (!token) { setJournalLoading(false); return; }
    setJournalLoading(true);
    let companyId = '';
    try { companyId = getActiveCompanyId(); } catch (e) { setJournalLoading(false); return; }
    if (journalCache && journalCache.companyId === companyId && Date.now() - journalCache.at < JOURNAL_CACHE_MS) {
      const cached = journalCache.entries;
      setEntries(prev => Array.from(new Map([...cached, ...prev].map(item => [item.id, item])).values()));
      setJournalLoading(false);
      return;
    }
    try {
      let res = await ObexRequestsHandlerP80.get(`/journal_entries_list?access_token=${encodeURIComponent(token)}&company_id=${encodeURIComponent(getActiveCompanyId())}`);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      const list = res.response || res.data || res;
      const accountsById = new Map(pgc.filter(c => c.id).map(c => [c.id as string, c]));
      if (Array.isArray(list) && list.length > 0) {
        // Una sola petición con todas las líneas; si no está disponible, una por asiento
        const linesByEntry = await listJournalLinesByEntry();
        const mapLine = (l: any): JournalLine => {
          const acc = accountsById.get(String(l.account_id));
          return {
            account: acc?.code || String(l.account_id ?? ''),
            accountName: acc?.name || `Cuenta #${l.account_id}`,
            debit: parseFloat(l.debit) || 0,
            credit: parseFloat(l.credit) || 0
          };
        };
        const mappedEntries: JournalEntry[] = await mapWithLimit(
          list,
          JOURNAL_DETAIL_CONCURRENCY,
          async (entry: any) => {
            let entryLines: JournalLine[] = [];
            if (linesByEntry) {
              entryLines = (linesByEntry.get(String(entry.id)) || []).map(mapLine);
            } else try {
              let detailRes = await ObexRequestsHandlerP80.get(`/journal_entries_get?access_token=${encodeURIComponent(token)}&id=${encodeURIComponent(String(entry.id))}`);
              if (detailRes.data) detailRes = detailRes.data;
              if (detailRes.result) detailRes = detailRes.result;
              const detail = detailRes.response || detailRes.data || detailRes;
              if (detail && Array.isArray(detail.lines)) {
                entryLines = detail.lines.map((l: any) => {
                  const acc = accountsById.get(String(l.account_id));
                  return {
                    account: acc?.code || String(l.account_id ?? ''),
                    accountName: acc?.name || `Cuenta #${l.account_id}`,
                    debit: parseFloat(l.debit) || 0,
                    credit: parseFloat(l.credit) || 0
                  };
                });
              }
            } catch (err) {
              console.error('Error fetching lines for journal entry', entry.id, err);
            }

            return {
              id: `AS-2026-${String(entry.id).padStart(3, '0')}`,
              date: entry.entry_date || new Date().toISOString().split('T')[0],
              concept: (entry.concept || 'Asiento Contable').toUpperCase(),
              status: entry.status || 'posted',
              lines: entryLines,
              sourceModule: entry.source_module || 'Libro Diario'
            };
          }
        );
        journalCache = { companyId, at: Date.now(), entries: mappedEntries };
        setEntries(prev => {
          const merged = [...mappedEntries, ...prev];
          const unique = Array.from(new Map(merged.map(item => [item.id, item])).values());
          return unique;
        });
      }
    } catch (e) {
      console.error('Error fetching journal entries from backend:', e);
    } finally {
      setJournalLoading(false);
    }
  };

  // Fetch Chart of Accounts from backend
  const fetchChartOfAccounts = async (): Promise<{ code: string; name: string; id?: string; level?: string }[]> => {
    const token = getAccessToken();
    if (!token) return [];
    try {
      let res = await ObexRequestsHandlerP80.get(`/chart_of_accounts_list?access_token=${encodeURIComponent(token)}&company_id=${encodeURIComponent(getActiveCompanyId())}`);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      const list = res.response || res.data || res;
      if (Array.isArray(list) && list.length > 0) {
        const mappedPgc = list.map((c: any) => ({
          code: String(c.code),
          name: c.name,
          id: c.id != null ? String(c.id) : undefined,
          level: c.level || undefined
        }));
        setChartOfAccounts(prev => {
          const merged = [...mappedPgc, ...prev];
          const unique = Array.from(new Map(merged.map(item => [item.code, item])).values());
          return unique;
        });
        return mappedPgc;
      }
    } catch (e) {
      console.error('Error fetching chart of accounts:', e);
    }
    return [];
  };

  // Create a new custom Chart of Accounts entry
  const createChartOfAccount = async (code: string, name: string, level: string, parentAccountId?: string) => {
    const token = getAccessToken();
    if (!token) return false;
    setCreatingAccount(true);
    try {
      let url = `/chart_of_accounts_create?access_token=${encodeURIComponent(token)}&company_id=${encodeURIComponent(getActiveCompanyId())}&code=${encodeURIComponent(code)}&name=${encodeURIComponent(name)}&level=${encodeURIComponent(level)}`;
      if (parentAccountId) url += `&parent_account_id=${encodeURIComponent(parentAccountId)}`;
      let res = await ObexRequestsHandlerP80.get(url);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      // res is now { response: ... }. A null/undefined response means the backend
      // accepted the call but did not actually create anything — must NOT be
      // treated as success (the insert can silently no-op on this endpoint).
      const created = res.response;
      if (created && typeof created === 'object') {
        setChartOfAccounts(prev => {
          const merged = [{ code: String(created.code ?? code), name: created.name ?? name, id: created.id != null ? String(created.id) : undefined }, ...prev];
          const unique = Array.from(new Map(merged.map(item => [item.code, item])).values());
          return unique;
        });
        setSelectedMayorAcc(String(created.code ?? code));
        showFeedback(`Cuenta contable ${code} — ${name} creada correctamente en el PGC.`);
        return true;
      }
      showFeedback(typeof created === 'string' ? created : 'El servidor no confirmó la creación de la cuenta contable (respuesta vacía). Puede ser un problema del backend.', true);
      return false;
    } catch (e) {
      console.error('Error creating chart of account:', e);
      showFeedback('Error al crear la cuenta contable.', true);
      return false;
    } finally {
      setCreatingAccount(false);
    }
  };

  // Real bank accounts — used for reconciliation (per-account movements) and for
  // the invoice edit form's "Cuenta Bancaria" selector.
  const [bankAccountsList, setBankAccountsList] = useState<{ id: string; name: string }[]>([]);

  // Fetch Bank Movements for reconciliation. bank_account_id is required by the
  // backend (movements are queried per account), so real account ids are fetched
  // first and movements are pulled per account and merged.
  const fetchBankMovements = async () => {
    const token = getAccessToken();
    if (!token) { setBankLoading(false); return; }
    setBankLoading(true);
    try {
      let acctRes = await ObexRequestsHandlerP80.get(`/bank_accounts_list?access_token=${encodeURIComponent(token)}&company_id=${getActiveCompanyId()}`);
      if (acctRes.data) acctRes = acctRes.data;
      if (acctRes.result) acctRes = acctRes.result;
      const accounts = acctRes.response || acctRes.data || acctRes;
      if (!Array.isArray(accounts) || accounts.length === 0) return;
      setBankAccountsList(accounts.map((a: any) => ({ id: String(a.id), name: a.name || `Cuenta #${a.id}` })));

      const results = await Promise.all(accounts.map(async (acc: any) => {
        try {
          let res = await ObexRequestsHandlerP80.get(`/bank_movements_list?access_token=${encodeURIComponent(token)}&bank_account_id=${encodeURIComponent(String(acc.id))}&reconciled=false`);
          if (res.data) res = res.data;
          if (res.result) res = res.result;
          const movements = res.response || res.data || res;
          return Array.isArray(movements) ? movements : [];
        } catch (e) {
          console.error(`Error fetching movements for bank account ${acc.id}:`, e);
          return [];
        }
      }));

      const mappedMovements = results.reduce((acc: any[], cur: any[]) => acc.concat(cur), []).map((m: any) => ({
        id: String(m.id),
        desc: m.description || 'Transacción Bancaria',
        amount: m.movement_type === 'expense' ? -Math.abs(parseFloat(m.amount) || 0) : Math.abs(parseFloat(m.amount) || 0),
        date: m.movement_date || new Date().toISOString().split('T')[0],
        defaultAccount: m.movement_type === 'expense' ? '629' : '430',
        catName: m.movement_type === 'expense' ? 'Otros servicios' : 'Clientes'
      }));
      setUnreconciledMovements(mappedMovements);

      // Facturas emitidas todavía sin cobrar: son las candidatas a cuadrar con
      // un ingreso. Se excluyen borradores, anuladas y ya cobradas.
      let openInvoices: any[] = [];
      try {
        let invRes = await ObexRequestsHandlerP80.get(`/invoices_list?access_token=${encodeURIComponent(token)}&limit=500`);
        if (invRes.data) invRes = invRes.data;
        if (invRes.result) invRes = invRes.result;
        const invList = invRes.response?.data || invRes.response || invRes.data || invRes;
        const closed = ['draft', 'void', 'voided', 'cancelled', 'collected', 'collected_negotiated'];

        // Facturas ya cuadradas con otro movimiento: reconcile no valida que la
        // factura esté libre, así que se excluyen aquí para no usarla dos veces.
        const usedInvoiceIds = new Set<string>();
        await Promise.all(accounts.map(async (acc: any) => {
          try {
            let recRes = await ObexRequestsHandlerP80.get(`/bank_movements_list?access_token=${encodeURIComponent(token)}&bank_account_id=${encodeURIComponent(String(acc.id))}&reconciled=true`);
            if (recRes.data) recRes = recRes.data;
            if (recRes.result) recRes = recRes.result;
            const recList = recRes.response || recRes.data || recRes;
            if (Array.isArray(recList)) {
              recList.forEach((m: any) => { if (m.reconciled_invoice_id) usedInvoiceIds.add(String(m.reconciled_invoice_id)); });
            }
          } catch (e) {
            console.error(`Error fetching reconciled movements for bank account ${acc.id}:`, e);
          }
        }));

        if (Array.isArray(invList)) {
          openInvoices = invList.filter((i: any) => !closed.includes(String(i.status)) && !usedInvoiceIds.has(String(i.id)));
        }
      } catch (e) {
        console.error('Error fetching open invoices for reconciliation:', e);
      }
      setBankOpenInvoices(openInvoices);

    } catch (e) {
      console.error('Error fetching unreconciled bank movements:', e);
    } finally {
      setBankLoading(false);
    }
  };

  // ---- Motor de propuestas de conciliación (etapa 1) ----------------------
  // Puntúa cada factura abierta contra cada ingreso: importe (exacto o con una
  // pequeña diferencia por comisión), número de factura o cliente citados en el
  // concepto del banco y cercanía de fechas. Se proponen solo coincidencias
  // claras (sin empate) y una misma factura nunca se propone a dos movimientos.
  const bankNorm = (v: any) => String(v || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Z0-9]/g, '');

  const scoreInvoiceForMovement = (mv: any, inv: any) => {
    const reasons: string[] = [];
    let score = 0;
    const amount = Math.abs(mv.amount);
    const total = parseFloat(inv.total) || 0;
    const diff = Math.abs(total - amount);
    if (total > 0 && diff < 0.005) { score += 60; reasons.push('importe exacto'); }
    else if (total > 0 && diff / total <= 0.03) { score += 20; reasons.push(`importe similar (dif. ${diff.toFixed(2)}€)`); }

    const desc = bankNorm(mv.desc);
    const num = bankNorm(inv.invoice_number);
    if (num.length >= 4 && desc.includes(num)) { score += 50; reasons.push('n.º de factura en el concepto'); }

    const client = clientsList.find(c => c.id === String(inv.client_id));
    if (client) {
      const cName = bankNorm(client.name);
      const cCif = bankNorm(client.cif);
      if ((cName.length >= 4 && desc.includes(cName)) || (cCif.length >= 5 && desc.includes(cCif))) {
        score += 25; reasons.push('cliente en el concepto');
      }
    }

    const refDate = inv.due_date || inv.issue_date || inv.created_at;
    if (refDate && mv.date) {
      const days = Math.abs((new Date(String(mv.date)).getTime() - new Date(String(refDate)).getTime()) / 86400000);
      if (!isNaN(days) && days <= 45) { score += 10; reasons.push('fecha cercana'); }
    }
    return { score, reasons };
  };

  useEffect(() => {
    const candidates: { mvId: string; invoiceId: string; score: number; reasons: string[]; second: number }[] = [];
    unreconciledMovements.forEach((mv: any) => {
      if (mv.amount <= 0) return; // los gastos no tienen factura que proponer
      const scored = bankOpenInvoices
        .map((inv: any) => ({ id: String(inv.id), ...scoreInvoiceForMovement(mv, inv) }))
        .filter(x => x.score >= 60)
        .sort((a, b) => b.score - a.score);
      if (scored.length === 0) return;
      const second = scored.length > 1 ? scored[1].score : 0;
      if (scored[0].score - second < 10) return; // empate: que decida el usuario
      candidates.push({ mvId: String(mv.id), invoiceId: scored[0].id, score: scored[0].score, reasons: scored[0].reasons, second });
    });

    // Una factura solo se propone una vez: gana el movimiento con mayor puntuación.
    candidates.sort((a, b) => b.score - a.score);
    const taken = new Set<string>();
    const proposals: Record<string, { invoiceId: string; score: number; reasons: string[] }> = {};
    candidates.forEach(c => {
      if (taken.has(c.invoiceId)) return;
      taken.add(c.invoiceId);
      proposals[c.mvId] = { invoiceId: c.invoiceId, score: c.score, reasons: c.reasons };
    });
    setBankProposals(proposals);

    // Preselecciona la propuesta solo donde el usuario aún no ha elegido nada.
    setBankMatch(prev => {
      const next = { ...prev };
      Object.keys(proposals).forEach(id => { if (next[id] === undefined) next[id] = proposals[id].invoiceId; });
      return next;
    });
  }, [unreconciledMovements, bankOpenInvoices, clientsList]);

  // Concilia un movimiento: 1) contabiliza el asiento (solo si no se hizo ya),
  // 2) lo marca como conciliado en el backend con la factura REAL elegida. Si
  // el paso 2 falla, el asiento ya existe: el movimiento queda pendiente y el
  // siguiente intento reintenta únicamente el marcado, sin duplicar el asiento.
  const handleReconcileMovement = async (btx: any): Promise<boolean> => {
    const token = getAccessToken();
    if (!token) { showFeedback('Sesión no válida: no se pudo conciliar.', true); return false; }

    const isIncome = btx.amount >= 0;
    const invoiceId = bankMatch[btx.id] || '';
    const invoice = bankOpenInvoices.find((i: any) => String(i.id) === invoiceId);
    if (isIncome && !invoiceId) {
      showFeedback('Selecciona la factura que cobra este ingreso antes de conciliar.', true);
      return false;
    }

    const counterCode = isIncome ? '430000' : (bankCounterAccount[btx.id] || btx.defaultAccount);
    const counterName = isIncome ? 'Clientes (Euros)' : (chartOfAccounts.find(c => c.code === counterCode)?.name || btx.catName);
    const amount = Math.abs(btx.amount);
    const invLabel = invoice ? (invoice.invoice_number || `#${invoice.id}`) : '';

    setBankBusyId(btx.id);
    let alreadyPosted = !!bankPosted[btx.id];
    try {
      if (!alreadyPosted) {
        const lines: JournalLine[] = isIncome ? [
          { account: '572000', accountName: 'Bancos c/c', debit: amount, credit: 0 },
          { account: counterCode, accountName: counterName, debit: 0, credit: amount }
        ] : [
          { account: counterCode, accountName: counterName, debit: amount, credit: 0 },
          { account: '572000', accountName: 'Bancos c/c', debit: 0, credit: amount }
        ];
        const posted = await addJournalEntry(`Conciliación bancaria: ${btx.desc}${invLabel ? ` (factura ${invLabel})` : ''}`, lines, 'Bancos para conciliación');
        if (!posted) return false; // addJournalEntry ya avisó del motivo; no se marca nada
        alreadyPosted = true;
        setBankPosted(prev => ({ ...prev, [btx.id]: true }));
      }

      let url = `/bank_movements_reconcile?access_token=${encodeURIComponent(token)}&id=${encodeURIComponent(String(btx.id))}`;
      if (invoiceId) url += `&reconciled_invoice_id=${encodeURIComponent(invoiceId)}`;
      let res = await ObexRequestsHandlerP80.get(url);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      const payload = res.response;
      if (!payload || typeof payload !== 'object' || payload.reconciled !== true) {
        const reason = typeof payload === 'string' ? payload : 'respuesta no válida del backend';
        showFeedback(`El asiento ya está contabilizado, pero el movimiento NO quedó marcado como conciliado (${reason}). Pulsa de nuevo para reintentar solo el marcado.`, true);
        return false;
      }

      setUnreconciledMovements(prev => prev.filter(m => m.id !== btx.id));
      setBankPosted(prev => { const next = { ...prev }; delete next[btx.id]; return next; });
      if (invoiceId) {
        // La factura ya está cuadrada: sale de las candidatas y de cualquier otra selección.
        setBankOpenInvoices(prev => prev.filter((i: any) => String(i.id) !== invoiceId));
        setBankMatch(prev => {
          const next: Record<string, string> = {};
          Object.keys(prev).forEach(k => { if (prev[k] !== invoiceId) next[k] = prev[k]; });
          return next;
        });
      }
      showFeedback(`Movimiento conciliado${invLabel ? ` con la factura ${invLabel}` : ''} y asiento contabilizado.`);
      return true;
    } catch (e) {
      console.error('Error reconciling bank movement:', e);
      showFeedback(alreadyPosted
        ? 'El asiento ya está contabilizado, pero falló el marcado del movimiento (error de red). Reintenta el marcado.'
        : 'Error de red al conciliar el movimiento.', true);
      return false;
    } finally {
      setBankBusyId(null);
    }
  };

  // Confirma de una vez todas las propuestas automáticas que el usuario no ha
  // cambiado. Va una a una (cada conciliación son dos llamadas al backend) y
  // se detiene en el primer fallo para no dejar varios movimientos a medias.
  const handleConfirmProposals = async () => {
    const targets = unreconciledMovements.filter((m: any) =>
      bankProposals[m.id] && bankMatch[m.id] === bankProposals[m.id].invoiceId && !bankPosted[m.id]);
    if (targets.length === 0) return;
    if (!window.confirm(`Se van a conciliar y contabilizar ${targets.length} movimiento(s) con la factura propuesta. ¿Continuar?`)) return;
    setBankBatchRunning(true);
    let done = 0;
    for (const m of targets) {
      const ok = await handleReconcileMovement(m);
      if (!ok) break;
      done++;
    }
    setBankBatchRunning(false);
    if (done === targets.length) showFeedback(`${done} movimiento(s) conciliado(s) y contabilizado(s).`);
    else showFeedback(`Se conciliaron ${done} de ${targets.length}; el proceso se detuvo en el primer fallo.`, true);
  };

  // ---- Bandeja de excepciones (etapa 2) ------------------------------------
  // Motivo por el que un movimiento no tiene propuesta automática. El motivo es
  // orientativo: sirve para que Ceci sepa por dónde empezar, no bloquea nada.
  const bankClassify = (mv: any): { kind: string; label: string; detail: string } => {
    const amount = Math.abs(mv.amount);
    if (mv.amount < 0) {
      return { kind: 'sin_factura', label: 'Gasto sin factura previa', detail: 'Comisión, impuesto o tasa: se asienta contra una cuenta de gasto.' };
    }
    const scored = bankOpenInvoices
      .map((inv: any) => ({ inv, ...scoreInvoiceForMovement(mv, inv) }))
      .filter(x => x.score >= 20)
      .sort((x, y) => y.score - x.score);
    const exact = bankOpenInvoices.filter((i: any) => Math.abs((parseFloat(i.total) || 0) - amount) < 0.005 && (parseFloat(i.total) || 0) > 0);
    if (exact.length > 1) {
      return { kind: 'ambiguo', label: 'Varias facturas con el mismo importe', detail: `${exact.length} facturas de ${amount.toFixed(2)}€: elige a cuál corresponde.` };
    }
    const similar = scored.find(x => x.reasons.some(r => r.startsWith('importe similar')));
    if (similar) {
      const t = parseFloat(similar.inv.total) || 0;
      return { kind: 'diferencia', label: 'Diferencia de importe o comisión', detail: `Se parece a ${similar.inv.invoice_number || `#${similar.inv.id}`} (${t.toFixed(2)}€), diferencia de ${(amount - t).toFixed(2)}€.` };
    }
    // Cobro agrupado: 2-4 facturas abiertas del mismo cliente que suman el ingreso.
    const byClient: Record<string, any[]> = {};
    bankOpenInvoices.forEach((i: any) => {
      const t = parseFloat(i.total) || 0;
      if (t > 0 && t < amount) (byClient[String(i.client_id)] = byClient[String(i.client_id)] || []).push(i);
    });
    const clientIds = Object.keys(byClient);
    for (let c = 0; c < clientIds.length; c++) {
      const list = byClient[clientIds[c]].slice(0, 20);
      const n = list.length;
      const sum = (idx: number[]) => idx.reduce((acc, k) => acc + (parseFloat(list[k].total) || 0), 0);
      const found = (idx: number[]) => Math.abs(sum(idx) - amount) < 0.005;
      const label = (idx: number[]) => idx.map(k => list[k].invoice_number || `#${list[k].id}`).join(' + ');
      for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
        if (found([i, j])) return { kind: 'agrupado', label: 'Posible cobro agrupado', detail: `Suma exacta de ${label([i, j])}. El backend solo admite una factura por movimiento.` };
        for (let k = j + 1; k < n; k++) {
          if (found([i, j, k])) return { kind: 'agrupado', label: 'Posible cobro agrupado', detail: `Suma exacta de ${label([i, j, k])}. El backend solo admite una factura por movimiento.` };
        }
      }
    }
    return { kind: 'impreciso', label: 'Sin coincidencia clara', detail: 'El concepto no identifica ninguna factura (concepto impreciso o cobro sin factura).' };
  };

  const renderBankCard = (btx: any) => {
                      const isIncome = btx.amount >= 0;
                      const absAmount = Math.abs(btx.amount);
                      const posted = !!bankPosted[btx.id];
                      const busy = bankBusyId === btx.id;
                      const selectedInvoice = bankOpenInvoices.find((i: any) => String(i.id) === (bankMatch[btx.id] || ''));
                      const selectedTotal = selectedInvoice ? (parseFloat(selectedInvoice.total) || 0) : 0;
                      const invoiceDiff = selectedInvoice ? absAmount - selectedTotal : 0;
                      const proposal = bankProposals[btx.id];
                      const sortedInvoices = [...bankOpenInvoices].sort((a: any, b: any) => {
                        const sa = scoreInvoiceForMovement(btx, a).score;
                        const sb = scoreInvoiceForMovement(btx, b).score;
                        if (sb !== sa) return sb - sa;
                        return Math.abs((parseFloat(a.total) || 0) - absAmount) - Math.abs((parseFloat(b.total) || 0) - absAmount);
                      });
                      return (
                      <div key={btx.id} className="fs-comp-134">
                        <div className="fs-comp-7">
                          <div className="fs-comp-14">
                            <span className="fs-comp-135">{btx.date}</span>
                            <span className="fs-comp-31">{btx.desc}</span>
                          </div>
                          {isIncome ? (
                            <p className="fs-comp-109">
                              COBRO DE FACTURA:{' '}
                              <select
                                value={bankMatch[btx.id] || ''}
                                disabled={posted || busy}
                                onChange={(e) => { const v = e.target.value; setBankMatch(prev => ({ ...prev, [btx.id]: v })); }}
                              >
                                <option value="">— Selecciona la factura —</option>
                                {sortedInvoices.map((inv: any) => {
                                  const t = parseFloat(inv.total) || 0;
                                  const cName = clientsList.find(c => c.id === String(inv.client_id))?.name || `Cliente #${inv.client_id}`;
                                  return (
                                    <option key={inv.id} value={String(inv.id)}>
                                      {Math.abs(t - absAmount) < 0.005 ? '✓ ' : ''}{inv.invoice_number || `#${inv.id}`} · {cName} · {t.toFixed(2)}€
                                    </option>
                                  );
                                })}
                              </select>
                            </p>
                          ) : (
                            <p className="fs-comp-109">
                              CUENTA DE GASTO:{' '}
                              <select
                                value={bankCounterAccount[btx.id] || btx.defaultAccount}
                                disabled={posted || busy}
                                onChange={(e) => { const v = e.target.value; setBankCounterAccount(prev => ({ ...prev, [btx.id]: v })); }}
                              >
                                {!chartOfAccounts.some(c => c.code === btx.defaultAccount) && (
                                  <option value={btx.defaultAccount}>{btx.defaultAccount} · {btx.catName}</option>
                                )}
                                {postableAccounts.map(c => (
                                  <option key={c.code} value={c.code}>{c.code} · {c.name}</option>
                                ))}
                              </select>
                            </p>
                          )}
                          {!proposal && (() => {
                            const why = bankClassify(btx);
                            return (
                              <p className="fs-comp-109" style={{ color: '#f59e0b' }}>
                                EXCEPCIÓN · {why.label.toUpperCase()}: {why.detail}
                              </p>
                            );
                          })()}
                          {proposal && (bankMatch[btx.id] || '') === proposal.invoiceId && !posted && (
                            <p className="fs-comp-109" style={{ color: '#10b981' }}>
                              PROPUESTA AUTOMÁTICA · PENDIENTE DE VALIDAR (confianza {proposal.score >= 100 ? 'alta' : 'media'}): {proposal.reasons.join(' · ')}
                            </p>
                          )}
                          {isIncome && selectedInvoice && Math.abs(invoiceDiff) >= 0.005 && (
                            <p className="fs-comp-109" style={{ color: '#f59e0b' }}>
                              DIFERENCIA de {invoiceDiff.toFixed(2)}€ entre el banco ({absAmount.toFixed(2)}€) y la factura ({selectedTotal.toFixed(2)}€) — se contabiliza el importe del banco.
                            </p>
                          )}
                          {isIncome && !bankMatch[btx.id] && bankOpenInvoices.length === 0 && (
                            <p className="fs-comp-109" style={{ color: '#f59e0b' }}>No hay facturas abiertas contra las que cuadrar este ingreso.</p>
                          )}
                          {posted && (
                            <p className="fs-comp-109" style={{ color: '#f59e0b' }}>
                              ASIENTO YA CONTABILIZADO — falta marcar el movimiento como conciliado en el backend.
                            </p>
                          )}
                        </div>

                        <div className="fs-comp-136">
                          <span className={cn("fs-comp-amount", btx.amount >= 0 ? "fs-amount-positive" : "fs-amount-negative")}>
                            {btx.amount >= 0 ? `+${btx.amount.toFixed(2)}€` : `${btx.amount.toFixed(2)}€`}
                          </span>

                          <button
                            disabled={busy || (isIncome && !bankMatch[btx.id])}
                            onClick={() => handleReconcileMovement(btx)}
                            className="fs-comp-137"
                          >
                            {busy ? 'Conciliando…' : posted ? 'Reintentar marcado' : 'Conciliar y Contabilizar'}
                          </button>
                        </div>
                      </div>
                      );
  };

  const chartPromiseRef = React.useRef<Promise<{ code: string; name: string; id?: string; level?: string }[]> | null>(null);
  const [journalRequested, setJournalRequested] = useState(false);

  useEffect(() => {
    chartPromiseRef.current = fetchChartOfAccounts();
    fetchBankMovements();
    fetchClientsList();
  }, []);

  // Save to LocalStorage on changes
  useEffect(() => {
    localStorage.setItem('albatross_asientos_contables', JSON.stringify(entries));
  }, [entries]);

  // Sidebar Sub-menu selection
  const [internalMenu, setInternalMenu] = useState<string>('review_entries');
  const activeMenu = controlledMenu ?? internalMenu;
  const setActiveMenu = (menuId: string) => {
    setInternalMenu(menuId);
    onChangeMenu?.(menuId);
  };

  // Los asientos (y el detalle de cada uno) solo se cargan al abrir una pantalla que los usa
  useEffect(() => {
    if (journalRequested || MENUS_WITHOUT_JOURNAL.includes(activeMenu)) return;
    setJournalRequested(true);
    (async () => {
      const pgc = chartPromiseRef.current ? await chartPromiseRef.current : [];
      await fetchJournalEntries(pgc);
    })();
  }, [activeMenu]);

  // Lazy-load the invoice history the first time that submenu is opened
  useEffect(() => {
    if (activeMenu === 'facturas_historial' && invoicesHistory.length === 0 && !invoicesHistoryLoading && !invoicesHistoryUnavailable) {
      fetchInvoicesHistory();
    }
  }, [activeMenu]);

  // --- Claves de amortización (catálogo maestro) -------------------------
  const [depreciationMethods, setDepreciationMethods] = useState<DepreciationMethod[]>([]);
  const [depMethodsLoading, setDepMethodsLoading] = useState(false);
  const [depMethodsLoaded, setDepMethodsLoaded] = useState(false);
  const [creatingDepMethod, setCreatingDepMethod] = useState(false);

  const fetchDepreciationMethods = async () => {
    setDepMethodsLoading(true);
    try {
      setDepreciationMethods(await listDepreciationMethods());
    } catch (e) {
      console.error('Error fetching depreciation methods:', e);
      setDepreciationMethods([]);
      showFeedback(e instanceof Error ? e.message : 'No se pudo cargar el catálogo de claves de amortización.', true);
    } finally {
      setDepMethodsLoading(false);
      setDepMethodsLoaded(true);
    }
  };

  useEffect(() => {
    if (activeMenu === 'depreciation_methods' && !depMethodsLoaded && !depMethodsLoading) {
      fetchDepreciationMethods();
    }
  }, [activeMenu]);

  // Skeletons for what comes from the API: the journal entries and the bank
  // movements pending reconciliation. Invoice cards share the same layout
  // (fs-comp-27/28/29), so renderEntriesSkeleton is reused for the history.
  const renderEntriesSkeleton = () => (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="fs-comp-27">
          <div className="fs-comp-28">
            <div className="fs-comp-29">
              <Skeleton width="72px" height="1.2em" />
              <Skeleton width="64px" height="1.2em" />
              <Skeleton width="180px" height="1.2em" />
            </div>
            <div className="fs-comp-29">
              <Skeleton width="88px" height="1.2em" />
              <Skeleton width="64px" height="1.2em" />
            </div>
          </div>
        </div>
      ))}
    </>
  );

  const renderBankMovementsSkeleton = () => (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="fs-comp-134">
          <div className="fs-comp-7">
            <div className="fs-comp-14">
              <Skeleton width="72px" height="1.2em" />
              <Skeleton width="200px" height="1.2em" />
            </div>
            <Skeleton width="88px" height="1.2em" />
          </div>
        </div>
      ))}
    </>
  );

  // Submenu Options list (Total 16 requested + Ledger Viewer)
  const submodules = [
    { id: 'review_entries', name: 'Libro Diario (Revisión)', desc: 'Revisión y ajuste de apuntes contables generales', icon: BookOpen, cat: 'SISTEMA CONTABLE' },
    { id: 'arrastre', name: 'Arrastre de Saldos', desc: 'Cierre y apertura automatizada de saldos contables', icon: ArrowRightLeft, cat: 'PROCESOS ANUALES' },
    { id: 'balance_sumas', name: 'Balance de Sumas y Saldos', desc: 'Cuadrante de saldos deudor/acreedor y sumas', icon: Scale, cat: 'DECLARACIONES E INFORMES' },
    { id: 'balance_situacion', name: 'Balance de Situación', desc: 'Activo, Pasivo y Patrimonio Neto a fecha de hoy', icon: Scale, cat: 'DECLARACIONES E INFORMES' },
    { id: 'pyg', name: 'Pérdidas y Ganancias', desc: 'Resultados del ejercicio económico actual', icon: FileSpreadsheet, cat: 'DECLARACIONES E INFORMES' },
    { id: 'mayor', name: 'Libro Mayor', desc: 'Consultas de extractos por cuentas del PGC', icon: FolderOpen, cat: 'SISTEMA CONTABLE' },
    { id: 'diario_docs', name: 'Diario de Documentos', desc: 'Trazabilidad de apuntes por documentos originales', icon: FileText, cat: 'SISTEMA CONTABLE' },
    { id: 'diario', name: 'Diario (Asiento Manual)', desc: 'Crear nuevos asientos de diario de doble entrada', icon: Plus, cat: 'SISTEMA CONTABLE' },
    { id: 'bancos', name: 'Bancos (Conciliación)', desc: 'Casar feed bancario con contabilidad general', icon: Landmark, cat: 'TESORERÍA Y BANCOS' },
    { id: 'factura_recibida', name: 'Factura Recibida', desc: 'Contabilizar facturas de compras a acreedores', icon: FileInput, cat: 'CONTABILIDAD DIARIA' },
    { id: 'factura_emitida', name: 'Factura Emitida', desc: 'Contabilizar facturas de ventas a clientes', icon: FileOutput, cat: 'CONTABILIDAD DIARIA' },
    { id: 'facturas_historial', name: 'Historial de Facturas', desc: 'Consulta de facturas emitidas y su detalle de líneas', icon: History, cat: 'CONTABILIDAD DIARIA' },
    { id: 'cierre', name: 'Cierre de Periodo', desc: 'Cerrar periodos mensuales, trimestrales o anuales', icon: ShieldCheck, cat: 'PROCESOS ANUALES' },
    { id: 'recibos', name: 'Recibos (Caja)', desc: 'Pequeños gastos y cobros al contado por caja', icon: Receipt, cat: 'CONTABILIDAD DIARIA' },
    { id: 'impuestos', name: 'Impuestos (AEAT)', desc: 'Cálculo de Modelos trimestrales 303 y 111', icon: Calculator, cat: 'DECLARACIONES E INFORMES' },
    { id: 'sepa', name: 'Cobros SEPA', desc: 'Remesas de adeudos directos (Norma 19 CSB)', icon: Coins, cat: 'TESORERÍA Y BANCOS' },
    { id: 'creditos', name: 'Créditos y Préstamos', desc: 'Financiación bancaria y cuadros de amortización', icon: Briefcase, cat: 'TESORERÍA Y BANCOS' },
    { id: 'pagos', name: 'Programas de Pago', desc: 'Remesas automáticas a acreedores (Norma 34)', icon: Key, cat: 'TESORERÍA Y BANCOS' },
    { id: 'activos_fijos', name: 'Activos Fijos', desc: 'Amortización de inmovilizado y bienes de inversión', icon: RefreshCw, cat: 'PROCESOS ANUALES' },
    { id: 'depreciation_methods', name: 'Claves de Amortización', desc: 'Catálogo de métodos y tasas de amortización', icon: Percent, cat: 'SISTEMA CONTABLE' }
  ];

  // Selected entry for viewing/editing
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editingFields, setEditingFields] = useState<JournalEntry | null>(null);

  useEffect(() => {
    setEditingEntryId(null);
    setEditingFields(null);
  }, [activeMenu]);
  const [selectedMayorAcc, setSelectedMayorAcc] = useState<string>('572000');
  const [showNewAccountForm, setShowNewAccountForm] = useState<boolean>(false);
  const [creatingAccount, setCreatingAccount] = useState<boolean>(false);
  // Periodo elegido en Cierre de periodo; la tabla de periodos contables muestra sus candados
  const [closePeriod, setClosePeriod] = useState<string>('Primer Trimestre 2026');

  // Success alert states
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const showFeedback = (msg: string, isError = false) => {
    if (isError) {
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(null), 5000);
    } else {
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(null), 4000);
    }
  };

  // Compara el código de una línea de asiento contra un código "objetivo"
  // sin exigir que coincidan letra a letra: sirve tanto si el PGC real está
  // sembrado más corto (subgrupo genérico, p.ej. '477') como más largo o
  // igual que el objetivo (p.ej. '477000' o una subcuenta '4770001').
  const accountMatches = (lineAccount: string, target: string) =>
    lineAccount === target || lineAccount.startsWith(target) || target.startsWith(lineAccount);

  // Automated Asiento Creator helper.
  //
  // journal_entries_post solo admite una pareja debe/haber por llamada: la
  // primera llamada (sin entry_id) crea la cabecera del asiento y contabiliza
  // el primer par; las siguientes (con el entry_id que devuelve la primera)
  // cuelgan más parejas de esa misma entrada en vez de crear una nueva. Un
  // asiento de N líneas se reparte así en varias llamadas encadenadas,
  // emparejando debe y haber por "cascada" (como conciliar cobros contra
  // facturas): no hace falta que haya el mismo número de líneas a cada lado,
  // solo que la suma cuadre — ya validado más abajo.
  const addJournalEntry = async (concept: string, lines: JournalLine[], sourceModule: string) => {
    // Basic zero balance validation
    const totalDebit = lines.reduce((acc, l) => acc + l.debit, 0);
    const totalCredit = lines.reduce((acc, l) => acc + l.credit, 0);
    const diff = Math.abs(totalDebit - totalCredit);

    if (diff > 0.01) {
      showFeedback(`Fallo al contabilizar: El asiento no cuadra (Debe: ${totalDebit.toFixed(2)}€, Haber: ${totalCredit.toFixed(2)}€).`, true);
      return false;
    }

    const token = getAccessToken();
    if (!token) {
      showFeedback('Sesión no válida: no se pudo contabilizar el asiento.', true);
      return false;
    }

    // Cada línea necesita el id numérico real de chart_of_accounts — el
    // código (p.ej. '700') no vale para account_debit_id/account_credit_id.
    // Muchos formularios de esta vista llevan un código de cuenta sugerido
    // escrito a mano (p.ej. '477000') que no tiene por qué coincidir letra a
    // letra con el que de verdad esté sembrado en el PGC (p.ej. '477') — así
    // que además de la igualdad exacta, se acepta la coincidencia por
    // subgrupo: la cuenta real cuyo código es el prefijo más largo del
    // pedido, o si no, cualquiera que empiece por el código pedido. Solo si
    // ninguna de las dos encuentra nada se aborta: mejor no contabilizar que
    // contabilizar a medias o contra una cuenta equivocada.
    const accountId = (code: string) => {
      const exact = chartOfAccounts.find(c => c.code === code);
      if (exact) return exact.id;

      let best: { id?: string; code: string } | null = null;
      for (const c of chartOfAccounts) {
        if (code.startsWith(c.code) && (!best || c.code.length > best.code.length)) best = c;
      }
      if (best) return best.id;

      const wider = chartOfAccounts.find(c => c.code.startsWith(code));
      return wider?.id;
    };
    const missing = new Set<string>();
    lines.forEach(l => { if ((l.debit > 0.005 || l.credit > 0.005) && !accountId(l.account)) missing.add(l.account); });
    if (missing.size > 0) {
      showFeedback(`No se puede contabilizar: la(s) cuenta(s) ${Array.from(missing).join(', ')} no existen en el plan contable cargado.`, true);
      return false;
    }

    type Pending = { accountId: string; remaining: number };
    const debitQueue: Pending[] = lines.filter(l => l.debit > 0.005).map(l => ({ accountId: accountId(l.account)!, remaining: l.debit }));
    const creditQueue: Pending[] = lines.filter(l => l.credit > 0.005).map(l => ({ accountId: accountId(l.account)!, remaining: l.credit }));

    const today = new Date().toISOString().split('T')[0];
    let entryId: string | null = null;

    try {
      while (debitQueue.length > 0 && creditQueue.length > 0) {
        const d = debitQueue[0];
        const c = creditQueue[0];
        const amount = Math.min(d.remaining, c.remaining);

        let url = `/journal_entries_post?access_token=${encodeURIComponent(token)}`;
        if (entryId) {
          url += `&entry_id=${encodeURIComponent(entryId)}`;
        } else {
          url += `&company_id=${encodeURIComponent(getActiveCompanyId())}&entry_date=${encodeURIComponent(today)}&concept=${encodeURIComponent(concept.toUpperCase())}&source_module=${encodeURIComponent(sourceModule)}`;
        }
        url += `&account_debit_id=${encodeURIComponent(d.accountId)}&account_credit_id=${encodeURIComponent(c.accountId)}&amount=${encodeURIComponent(amount.toFixed(2))}`;

        let res = await ObexRequestsHandlerP80.get(url);
        if (res.data) res = res.data;
        if (res.result) res = res.result;
        const record = res.response;
        if (!record || typeof record !== 'object' || Array.isArray(record) || !record.id) {
          throw new Error(typeof record === 'string' ? record : 'El backend no confirmó la entrada contable.');
        }
        entryId = String(record.id);

        d.remaining -= amount;
        c.remaining -= amount;
        if (d.remaining <= 0.005) debitQueue.shift();
        if (c.remaining <= 0.005) creditQueue.shift();
      }
    } catch (err) {
      console.error('Error posting journal entry to backend:', err);
      showFeedback(err instanceof Error ? `Fallo al contabilizar: ${err.message}` : 'Error al contabilizar el asiento.', true);
      return false;
    }

    const nextIdNum = entries.length + 1;
    const newEntry: JournalEntry = {
      id: `AS-2026-${String(nextIdNum).padStart(3, '0')}`,
      date: today,
      concept: concept.toUpperCase(),
      status: 'posted',
      lines: lines,
      sourceModule: sourceModule
    };

    setEntries(prev => [...prev, newEntry]);
    showFeedback(`¡Asiento contable ${newEntry.id} registrado con éxito! Concepto: ${concept}`);
    return true;
  };

  // Delete/Rollback entry
  const deleteEntry = (id: string) => {
    setDeleteModalItem(id);
  };

  const confirmDelete = () => {
    if (!deleteModalItem) return;
    setEntries(prev => prev.filter(e => e.id !== deleteModalItem));
    showFeedback(`Asiento ${deleteModalItem} anulado del Libro Diario.`);
    setDeleteModalItem(null);
  };

  // Begin Edit Mode for ledger entry
  const startEditEntry = (entry: JournalEntry) => {
    setEditingEntryId(entry.id);
    setEditingFields(JSON.parse(JSON.stringify(entry))); // deep copy
  };

  const handleEditLineChange = (index: number, field: keyof JournalLine, value: any) => {
    if (!editingFields) return;
    const linesCopy = [...editingFields.lines];
    if (field === 'account') {
      const pgcAcc = chartOfAccounts.find(c => c.code === value);
      linesCopy[index].account = value;
      linesCopy[index].accountName = pgcAcc ? pgcAcc.name : 'Otra cuenta';
    } else if (field === 'debit' || field === 'credit') {
      linesCopy[index][field] = parseFloat(value) || 0;
    }
    setEditingFields({ ...editingFields, lines: linesCopy });
  };

  const saveEditedEntry = () => {
    if (!editingFields) return;
    const totalDebit = editingFields.lines.reduce((acc, l) => acc + l.debit, 0);
    const totalCredit = editingFields.lines.reduce((acc, l) => acc + l.credit, 0);
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      showFeedback(`No cuadra el asiento. Desfase de ${(totalDebit - totalCredit).toFixed(2)}€.`, true);
      return;
    }
    setEntries(prev => prev.map(e => e.id === editingFields.id ? editingFields : e));
    setEditingEntryId(null);
    setEditingFields(null);
    showFeedback(`Cambios aplicados correctamente al asiento ${editingFields.id}.`);
  };

  // Format currency with privacy option
  const formatCurrency = (val: number) => {
    if (privacyMode) return '•••••€';
    return `${val.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  };

  // Group submodules by category
  const categories = Array.from(new Set(submodules.map(s => s.cat)));

  return (
    <div className={cn("fs-comp-1", hideSidebar && "fs-suite-embedded")}>

      {/* 1. SIDEBAR SUBMENU: List of the 16 sub-modules */}
      {!hideSidebar && (
      <div className="fs-comp-2">
        <div className="fs-comp-3">
          <span className="fs-comp-4">// ADMINISTRACIÓN FINANCIERA</span>
          <h4 className="fs-comp-5">SISTEMA GENERAL CONTABLE</h4>
        </div>

        <div className="fs-comp-6">
          {categories.map(cat => (
            <div key={cat} className="fs-comp-7">
              <span className="fs-comp-8">{cat}</span>
              <div className="fs-comp-9">
                {submodules.filter(s => s.cat === cat).map(sub => {
                  const Icon = sub.icon;
                  const isActive = activeMenu === sub.id;
                  return (
                    <button
                      key={sub.id}
                      onClick={() => {
                        setActiveMenu(sub.id);
                        setEditingEntryId(null);
                        setEditingFields(null);
                      }}
                      className={cn(
                        "fs-sidebar-btn",
                        isActive ? "active" : "inactive"
                      )}
                    >
                      <Icon size={12} className="fs-sidebar-icon" />
                      <div className="fs-comp-10">
                        <span className="fs-comp-11">{sub.name}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* 2. MAIN ACTIVE WORKSPACE PANEL */}
      <div className="fs-comp-12">
        
        <div>
          {/* Header of Active workspace */}
          <div className="fs-comp-13">
            <div>
              <div className="fs-comp-14">
                <div className="fs-comp-15" />
                <h3 className="fs-comp-16">
                  {headerTitle ?? submodules.find(s => s.id === activeMenu)?.name}
                </h3>
              </div>
              <p className="fs-comp-17">
                {headerDesc ?? submodules.find(s => s.id === activeMenu)?.desc}
              </p>
            </div>
            
            {/* Feedback system */}
            <div className="fs-comp-18">
              
                {successMsg && (
                  <div
                    className="fs-comp-19"
                  >
                    {successMsg}
                  </div>
                )}
                {errorMsg && (
                  <div
                    className="fs-comp-20"
                  >
                    {errorMsg}
                  </div>
                )}
              
            </div>
          </div>

          {/* DYNAMIC RENDERING OF SUBMODULE FORMS & LOGIC */}
          <div className="fs-comp-21">

            {/* ========================================================= */}
            {/* 1. LIBRO DIARIO - REVISIÓN DE APUNTES CONTABLES (GENERAL) */}
            {/* ========================================================= */}
            {activeMenu === 'review_entries' && (
              <div className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-23">
                    <span className="fs-comp-24">PUNTO GENERAL DE AUDITORÍA Y AJUSTES CONTABLES:</span><br />
                    A continuación se listan cronológicamente todos los asientos del Libro Diario. Use este visor para revisar, editar o anular cualquier apunte. El sistema exige y valida la <span className="fs-comp-25">Partida Doble (∑ Debe = ∑ Haber)</span> en tiempo real antes de guardar cambios.
                  </p>
                </div>

                <div className="fs-comp-26">
                  {journalLoading ? renderEntriesSkeleton() : entries.slice().reverse().map((entry) => {
                    const isEditing = editingEntryId === entry.id;
                    const totalDebit = entry.lines.reduce((acc, l) => acc + l.debit, 0);
                    const totalCredit = entry.lines.reduce((acc, l) => acc + l.credit, 0);

                    return (
                      <div key={entry.id} className="fs-comp-27">
                        {/* Entry Header */}
                        <div className="fs-comp-28">
                          <div className="fs-comp-29">
                            <span className="fs-comp-25">{entry.id}</span>
                            <span className="fs-comp-30">{entry.date}</span>
                            <span className="fs-comp-31">{entry.concept}</span>
                          </div>
                          
                          <div className="fs-comp-29">
                            {entry.sourceModule && (
                              <span className="fs-comp-32">
                                {entry.sourceModule}
                              </span>
                            )}
                            <span className="fs-comp-25">{formatCurrency(totalDebit)}</span>
                            
                            {!isEditing ? (
                              <div className="fs-comp-33">
                                <button 
                                  onClick={() => startEditEntry(entry)}
                                  className="fs-comp-34"
                                  title="Editar asiento contable"
                                >
                                  <Edit3 size={11} /> Ajustar
                                </button>
                                <button 
                                  onClick={() => deleteEntry(entry.id)}
                                  className="fs-comp-35"
                                  title="Anular asiento"
                                >
                                  <Trash2 size={11} /> Anular
                                </button>
                              </div>
                            ) : (
                              <div className="fs-comp-33">
                                <button 
                                  onClick={saveEditedEntry}
                                  className="fs-comp-36"
                                >
                                  <Save size={10} /> Guardar
                                </button>
                                <button 
                                  onClick={() => { setEditingEntryId(null); setEditingFields(null); }}
                                  className="fs-comp-37"
                                >
                                  Cancelar
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Entry Body (Lines) */}
                        <div className="fs-comp-38">
                          <table className="fs-comp-39">
                            <thead>
                              <tr className="fs-comp-40">
                                <th className="fs-comp-41">Cuenta</th>
                                <th className="fs-comp-42">Denominación del PGC</th>
                                <th className="fs-comp-43">Debe (Dr)</th>
                                <th className="fs-comp-43">Haber (Cr)</th>
                              </tr>
                            </thead>
                            <tbody className="fs-comp-44">
                              {!isEditing ? (
                                entry.lines.map((line, idx) => (
                                  <tr key={idx} className="fs-comp-45">
                                    <td className="fs-comp-46">{line.account}</td>
                                    <td className="fs-comp-47">{line.accountName}</td>
                                    <td className="fs-comp-48">
                                      {line.debit > 0 ? formatCurrency(line.debit) : '—'}
                                    </td>
                                    <td className="fs-comp-49">
                                      {line.credit > 0 ? formatCurrency(line.credit) : '—'}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                editingFields?.lines.map((line, idx) => (
                                  <tr key={idx} className="fs-comp-50">
                                    <td className="fs-comp-51">
                                      <select 
                                        value={line.account} 
                                        onChange={(e) => handleEditLineChange(idx, 'account', e.target.value)}
                                        className="fs-comp-52"
                                      >
                                        {postableAccounts.map(c => (
                                          <option key={c.code} value={c.code}>{c.code}</option>
                                        ))}
                                      </select>
                                    </td>
                                    <td className="fs-comp-53">{line.accountName}</td>
                                    <td className="fs-comp-54">
                                      <input 
                                        type="number"
                                        step="0.01"
                                        value={line.debit || ''}
                                        onChange={(e) => handleEditLineChange(idx, 'debit', e.target.value)}
                                        className="fs-comp-55"
                                        placeholder="0.00"
                                      />
                                    </td>
                                    <td className="fs-comp-54">
                                      <input 
                                        type="number"
                                        step="0.01"
                                        value={line.credit || ''}
                                        onChange={(e) => handleEditLineChange(idx, 'credit', e.target.value)}
                                        className="fs-comp-55"
                                        placeholder="0.00"
                                      />
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* 2. ARRASTRE DE SALDOS (Balance Carry Forward) */}
            {/* ========================================================= */}
            {activeMenu === 'arrastre' && (
              <form onSubmit={async (e: any) => {
                e.preventDefault();
                const concept = e.target.concept.value || "Arrastre de saldos y apertura 2026";
                // Generate opening entry lines (Grupos 1 a 5 balances)
                const carryLines: JournalLine[] = [
                  { account: '217000', accountName: 'Equipos para procesos de información', debit: 4500.00, credit: 0 },
                  { account: '300000', accountName: 'Mercaderías', debit: 8000.00, credit: 0 },
                  { account: '572000', accountName: 'Bancos c/c (WISE EUR)', debit: 32000.00, credit: 0 },
                  { account: '100000', accountName: 'Capital Social', debit: 0, credit: 35000.00 },
                  { account: '129000', accountName: 'Resultado del Ejercicio', debit: 0, credit: 9500.00 }
                ];
                const ok = await addJournalEntry(concept, carryLines, 'Arrastre de Saldos');
                if (ok) setActiveMenu('review_entries');
              }} className="fs-comp-21">
                <div className="fs-comp-56">
                  <h4 className="fs-comp-31">Consolidación y Cierre de Ejercicio Anterior (2025)</h4>
                  <p className="fs-comp-57">
                    Este asistente calcula el saldo final de las cuentas de balance (patrimonio, activo y pasivo: grupos 1 al 5) del ejercicio 2025 y genera automáticamente el <span className="fs-comp-25">Asiento de Apertura</span> en la fecha de inicio del ejercicio 2026.
                  </p>
                </div>

                <div className="fs-comp-58">
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Ejercicio de Cierre Origen</label>
                    <input type="text" readOnly value="2025" className="fs-comp-61" />
                  </div>
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Ejercicio de Apertura Destino</label>
                    <input type="text" readOnly value="2026" className="fs-comp-61" />
                  </div>
                  <div className="fs-comp-62">
                    <label className="fs-comp-60">Concepto del Asiento Contable</label>
                    <input name="concept" required defaultValue="ASIENTO DE APERTURA DE EJERCICIO CONTABLE 2026" type="text" className="fs-comp-63" />
                  </div>
                </div>

                {/* Simulated table preview of balances to carry forward */}
                <div className="fs-comp-64">
                  <span className="fs-comp-65">Vista Previa de Cuentas del Balance a Traspasar</span>
                  <div className="fs-comp-66">
                    <table className="fs-comp-67">
                      <thead className="fs-comp-68">
                        <tr>
                          <th className="fs-comp-69">Cuenta PGC</th>
                          <th className="fs-comp-69">Descripción</th>
                          <th className="fs-comp-70">Debe (Deudor 2025)</th>
                          <th className="fs-comp-70">Haber (Acreedor 2025)</th>
                        </tr>
                      </thead>
                      <tbody className="fs-comp-71">
                        <tr>
                          <td className="fs-comp-72">217000</td>
                          <td className="fs-comp-73">Equipos para procesos de información</td>
                          <td className="fs-comp-74">4.500,00 €</td>
                          <td className="fs-comp-70">—</td>
                        </tr>
                        <tr>
                          <td className="fs-comp-72">300000</td>
                          <td className="fs-comp-73">Mercaderías</td>
                          <td className="fs-comp-74">8.000,00 €</td>
                          <td className="fs-comp-70">—</td>
                        </tr>
                        <tr>
                          <td className="fs-comp-72">572000</td>
                          <td className="fs-comp-73">Bancos c/c (WISE EUR)</td>
                          <td className="fs-comp-74">32.000,00 €</td>
                          <td className="fs-comp-70">—</td>
                        </tr>
                        <tr>
                          <td className="fs-comp-72">100000</td>
                          <td className="fs-comp-73">Capital Social</td>
                          <td className="fs-comp-70">—</td>
                          <td className="fs-comp-75">35.000,00 €</td>
                        </tr>
                        <tr>
                          <td className="fs-comp-72">129000</td>
                          <td className="fs-comp-73">Resultado del Ejercicio</td>
                          <td className="fs-comp-70">—</td>
                          <td className="fs-comp-75">9.500,00 €</td>
                        </tr>
                        <tr className="fs-comp-76">
                          <td colSpan={2} className="fs-comp-77">TOTAL ARRASTRE CUADRADO:</td>
                          <td className="fs-comp-78">44.500,00 €</td>
                          <td className="fs-comp-78">44.500,00 €</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="fs-comp-79">
                  <button type="submit" className="fs-comp-80">
                    Ejecutar Arrastre de Saldos y Contabilizar Apertura 2026
                  </button>
                </div>
              </form>
            )}

            {/* ========================================================= */}
            {/* 3. BALANCE DE SUMAS Y SALDOS (Trial Balance) */}
            {/* ========================================================= */}
            {/* ========================================================= */}
            {/* CLAVES DE AMORTIZACIÓN (Catálogo maestro) */}
            {/* ========================================================= */}
            {activeMenu === 'depreciation_methods' && (
              <div className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Catálogo de <span className="fs-comp-24">claves de amortización</span> que consumen las valoraciones de los activos fijos. Cada clave define el método de cálculo (lineal o degresivo) y su tasa anual. El backend solo permite consultar y dar de alta: las claves ya usadas por una valoración no se editan ni se borran.
                  </p>
                </div>

                <div className="fs-comp-26">
                  <div className="fs-comp-81">
                    <span className="fs-comp-31">
                      Catálogo de Claves ({depreciationMethods.length})
                    </span>
                    <button onClick={fetchDepreciationMethods} className="fs-comp-82">
                      Recargar
                    </button>
                  </div>

                  <div className="fs-comp-66">
                    <table className="fs-comp-83">
                      <thead className="fs-comp-68">
                        <tr className="fs-comp-84">
                          <th className="fs-comp-85">Código</th>
                          <th className="fs-comp-69">Denominación</th>
                          <th className="fs-comp-69">Tipo</th>
                          <th className="fs-comp-86">Tasa Anual</th>
                          <th className="fs-comp-86">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="fs-comp-71">
                        {depMethodsLoading ? (
                          Array.from({ length: 4 }).map((_, i) => (
                            <tr key={i} className="fs-comp-87">
                              <td className="fs-comp-72"><Skeleton height="1.2em" /></td>
                              <td className="fs-comp-88"><Skeleton height="1.2em" /></td>
                              <td className="fs-comp-88"><Skeleton height="1.2em" /></td>
                              <td className="fs-comp-70"><Skeleton height="1.2em" /></td>
                              <td className="fs-comp-70"><Skeleton height="1.2em" /></td>
                            </tr>
                          ))
                        ) : depreciationMethods.length === 0 ? (
                          <tr className="fs-comp-87">
                            <td colSpan={5} className="fs-comp-88">No hay claves de amortización dadas de alta.</td>
                          </tr>
                        ) : (
                          depreciationMethods.map(method => (
                            <tr key={method.id} className="fs-comp-87">
                              <td className="fs-comp-72">{method.code}</td>
                              <td className="fs-comp-88">{method.name}</td>
                              <td className="fs-comp-88">{DEPRECIATION_METHOD_TYPE_LABELS[method.methodType]}</td>
                              <td className="fs-comp-70">{method.annualRatePct}%</td>
                              <td className="fs-comp-70">{method.status === 'active' ? 'ACTIVA' : 'INACTIVA'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <form
                  onSubmit={async (e: any) => {
                    e.preventDefault();
                    const code = e.target.depCode.value.trim().toUpperCase();
                    const name = e.target.depName.value.trim();
                    const methodType = e.target.depType.value;
                    const annualRatePct = Number(e.target.depRate.value);

                    if (!code || !name) {
                      showFeedback('Código y denominación son obligatorios.', true);
                      return;
                    }
                    if (isNaN(annualRatePct) || annualRatePct <= 0 || annualRatePct > 100) {
                      showFeedback('La tasa anual debe estar entre 0 y 100.', true);
                      return;
                    }

                    setCreatingDepMethod(true);
                    try {
                      const created = await createDepreciationMethod({ code, name, methodType, annualRatePct });
                      setDepreciationMethods(prev => [...prev, created]);
                      e.target.reset();
                      showFeedback(`Clave de amortización ${created.code} creada correctamente.`);
                    } catch (err) {
                      console.error('Error creating depreciation method:', err);
                      showFeedback(err instanceof Error ? err.message : 'Error al crear la clave de amortización.', true);
                    } finally {
                      setCreatingDepMethod(false);
                    }
                  }}
                  className="fs-comp-22"
                >
                  <div className="fs-comp-138">
                    <div className="fs-comp-59">
                      <label className="fs-comp-60">Código</label>
                      <input name="depCode" required placeholder="EJ: AM-LIN-10" type="text" className="fs-comp-127" />
                    </div>
                    <div className="fs-comp-59">
                      <label className="fs-comp-60">Denominación</label>
                      <input name="depName" required placeholder="EJ: Lineal 10 años" type="text" className="fs-comp-127" />
                    </div>
                    <div className="fs-comp-59">
                      <label className="fs-comp-60">Tipo de cálculo</label>
                      <select name="depType" defaultValue="linear" className="fs-comp-63">
                        <option value="linear">Lineal</option>
                        <option value="degressive">Degresivo</option>
                      </select>
                    </div>
                    <div className="fs-comp-59">
                      <label className="fs-comp-60">Tasa anual (%)</label>
                      <input name="depRate" required defaultValue="10" min="0.01" max="100" step="0.01" type="number" className="fs-comp-127" />
                    </div>
                  </div>
                  <div className="fs-comp-79">
                    <button type="submit" disabled={creatingDepMethod} className="fs-comp-107">
                      {creatingDepMethod ? 'Creando…' : 'Crear Clave de Amortización'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeMenu === 'balance_sumas' && (
              <div className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Este cuadrante calcula dinámicamente las sumas acumuladas de Debe y Haber y los saldos netos resultantes (Deudores o Acreedores) de todas las cuentas operativas en base al <span className="fs-comp-24">Libro Diario actual</span>. Sometido a legislación mercantil española.
                  </p>
                </div>

                {/* Calculate sums and balances dynamically from our state */}
                {(() => {
                  const accountMap: Record<string, { name: string, debe: number, haber: number }> = {};
                  chartOfAccounts.forEach(c => {
                    accountMap[c.code] = { name: c.name, debe: 0, haber: 0 };
                  });

                  entries.forEach(e => {
                    e.lines.forEach(l => {
                      if (!accountMap[l.account]) {
                        accountMap[l.account] = { name: l.accountName, debe: 0, haber: 0 };
                      }
                      accountMap[l.account].debe += l.debit;
                      accountMap[l.account].haber += l.credit;
                    });
                  });

                  let totalDebe = 0;
                  let totalHaber = 0;
                  let totalDeudor = 0;
                  let totalAcreedor = 0;

                  const tableRows = Object.entries(accountMap)
                    .filter(([_, data]) => data.debe > 0 || data.haber > 0)
                    .map(([code, data]) => {
                      const net = data.debe - data.haber;
                      const deudor = net > 0 ? net : 0;
                      const acreedor = net < 0 ? Math.abs(net) : 0;

                      totalDebe += data.debe;
                      totalHaber += data.haber;
                      totalDeudor += deudor;
                      totalAcreedor += acreedor;

                      return { code, name: data.name, debe: data.debe, haber: data.haber, deudor, acreedor };
                    });

                  return (
                    <div className="fs-comp-26">
                      <div className="fs-comp-81">
                        <span className="fs-comp-31">Ejercicio Económico 2026 (Saldos Consolidados)</span>
                        <button 
                          onClick={() => showFeedback("Exportando Balance de Sumas y Saldos a formato oficial BOE...")}
                          className="fs-comp-82"
                        >
                          Exportar PDF/Borrador
                        </button>
                      </div>

                      <div className="fs-comp-66">
                        <table className="fs-comp-83">
                          <thead className="fs-comp-68">
                            <tr className="fs-comp-84">
                              <th className="fs-comp-85">Cuenta</th>
                              <th className="fs-comp-69">Denominación de la Cuenta</th>
                              <th className="fs-comp-86">Suma Debe</th>
                              <th className="fs-comp-86">Suma Haber</th>
                              <th className="fs-comp-86">S. Deudor</th>
                              <th className="fs-comp-86">S. Acreedor</th>
                            </tr>
                          </thead>
                          <tbody className="fs-comp-71">
                            {tableRows.map(row => (
                              <tr key={row.code} className="fs-comp-87">
                                <td className="fs-comp-72">{row.code}</td>
                                <td className="fs-comp-88">{row.name}</td>
                                <td className="fs-comp-70">{row.debe > 0 ? formatCurrency(row.debe) : '—'}</td>
                                <td className="fs-comp-70">{row.haber > 0 ? formatCurrency(row.haber) : '—'}</td>
                                <td className="fs-comp-89">{row.deudor > 0 ? formatCurrency(row.deudor) : '—'}</td>
                                <td className="fs-comp-90">{row.acreedor > 0 ? formatCurrency(row.acreedor) : '—'}</td>
                              </tr>
                            ))}
                            <tr className="fs-comp-91">
                              <td colSpan={2} className="fs-comp-92">Sumas Totales del Balance:</td>
                              <td className="fs-comp-78">{formatCurrency(totalDebe)}</td>
                              <td className="fs-comp-78">{formatCurrency(totalHaber)}</td>
                              <td className="fs-comp-74">{formatCurrency(totalDeudor)}</td>
                              <td className="fs-comp-75">{formatCurrency(totalAcreedor)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ========================================================= */}
            {/* BALANCE DE SITUACIÓN                                      */}
            {/* ========================================================= */}
            {activeMenu === 'balance_situacion' && (
              <div className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Representación patrimonial y financiera consolidada a fecha de hoy en base al <span className="fs-comp-24">Libro Diario actual</span>. Estructura adaptada al Plan General de Contabilidad (PGC), con verificación en tiempo real de la <span className="fs-comp-24">ecuación contable fundamental (Activo = Pasivo + Patrimonio Neto)</span>.
                  </p>
                </div>

                {(() => {
                  // 1. Build balances for all accounts
                  const accountMap: Record<string, { name: string; debe: number; haber: number }> = {};
                  chartOfAccounts.forEach(c => {
                    accountMap[c.code] = { name: c.name, debe: 0, haber: 0 };
                  });

                  entries.forEach(e => {
                    e.lines.forEach(l => {
                      if (!accountMap[l.account]) {
                        accountMap[l.account] = { name: l.accountName || `Cuenta ${l.account}`, debe: 0, haber: 0 };
                      }
                      accountMap[l.account].debe += l.debit;
                      accountMap[l.account].haber += l.credit;
                    });
                  });

                  // 2. Compute "Resultado del Ejercicio" (P&G integration)
                  // Total 7 (credit - debit) - Total 6 (debit - credit) + any balance in account 129
                  let totalIngresos7 = 0;
                  let totalGastos6 = 0;
                  let saldo129 = 0;

                  Object.entries(accountMap).forEach(([code, data]) => {
                    if (code.startsWith('7')) {
                      totalIngresos7 += (data.haber - data.debe);
                    } else if (code.startsWith('6')) {
                      totalGastos6 += (data.debe - data.haber);
                    } else if (code.startsWith('129')) {
                      saldo129 += (data.haber - data.debe);
                    }
                  });

                  const resultadoEjercicio = saldo129 + (totalIngresos7 - totalGastos6);

                  // 3. Classify into Balance sections
                  // ACTIVO NO CORRIENTE — desglosado por subgrupo, no por grupo entero,
                  // para reflejar los epígrafes I-VI del Modelo Normal de Balance.
                  let inmovilizadoIntangible = 0;      // subgrupo 20
                  let inmovilizadoMaterial = 0;        // subgrupos 21/23 (incl. en curso)
                  let inversionesInmobiliarias = 0;    // subgrupo 22
                  let inversionesFinancierasLP = 0;    // subgrupos 24/25/26
                  let amortizacionAcumulada = 0;       // subgrupos 28/29
                  const inmovilizadoIntangibleRows: { code: string; name: string; amount: number }[] = [];
                  const inmovilizadoMaterialRows: { code: string; name: string; amount: number }[] = [];
                  const inversionesInmobiliariasRows: { code: string; name: string; amount: number }[] = [];
                  const inversionesFinancierasLPRows: { code: string; name: string; amount: number }[] = [];
                  const amortizacionRows: { code: string; name: string; amount: number }[] = [];

                  // ACTIVO CORRIENTE
                  let existenciasTotal = 0;
                  const existenciasRows: { code: string; name: string; amount: number }[] = [];

                  let clientesTotal = 0;
                  const clientesRows: { code: string; name: string; amount: number }[] = [];

                  let hpDeudoraTotal = 0;
                  const hpDeudoraRows: { code: string; name: string; amount: number }[] = [];

                  let tesoreriaTotal = 0;
                  const tesoreriaRows: { code: string; name: string; amount: number }[] = [];

                  let otrosActivosTotal = 0;
                  const otrosActivosRows: { code: string; name: string; amount: number }[] = [];

                  let activosMantenidosVentaTotal = 0;    // subgrupo 58
                  const activosMantenidosVentaRows: { code: string; name: string; amount: number }[] = [];

                  let periodificacionesActivoTotal = 0;   // subgrupo 48, lado deudor
                  const periodificacionesActivoRows: { code: string; name: string; amount: number }[] = [];

                  // PATRIMONIO NETO — A-1 Fondos Propios se desglosa en Capital/Reservas/
                  // Resultados de ejercicios anteriores; A-2 y A-3 van aparte, como exige
                  // la estructura oficial (no son parte de Fondos Propios).
                  let capitalTotal = 0;                // subgrupo 10
                  let reservasTotal = 0;                // subgrupo 11
                  let resultadosAnterioresTotal = 0;    // subgrupo 12 (excl. 129, tratado en P&G)
                  let otrosFondosPropiosTotal = 0;      // resto del grupo 1 no clasificado (136/137/139...)
                  let ajustesCambioValorTotal = 0;      // 133/138 — A-2
                  let subvencionesTotal = 0;            // 130/131/132 — A-3
                  const capitalRows: { code: string; name: string; amount: number }[] = [];
                  const reservasRows: { code: string; name: string; amount: number }[] = [];
                  const resultadosAnterioresRows: { code: string; name: string; amount: number }[] = [];
                  const otrosFondosPropiosRows: { code: string; name: string; amount: number }[] = [];
                  const ajustesCambioValorRows: { code: string; name: string; amount: number }[] = [];
                  const subvencionesRows: { code: string; name: string; amount: number }[] = [];

                  // PASIVO NO CORRIENTE — provisiones (14x) separadas de las deudas, y
                  // dentro de estas, con empresas del grupo (16x) aparte de entidades de
                  // crédito y otros (17x/18x), como exige la estructura oficial.
                  let provisionesLPTotal = 0;
                  let deudasGrupoLPTotal = 0;
                  let deudasEntidadesLPTotal = 0;
                  const provisionesLPRows: { code: string; name: string; amount: number }[] = [];
                  const deudasGrupoLPRows: { code: string; name: string; amount: number }[] = [];
                  const deudasEntidadesLPRows: { code: string; name: string; amount: number }[] = [];

                  // PASIVO CORRIENTE
                  let proveedoresTotal = 0;
                  const proveedoresRows: { code: string; name: string; amount: number }[] = [];

                  let deudasCortoPlazoTotal = 0;
                  const deudasCortoPlazoRows: { code: string; name: string; amount: number }[] = [];

                  let hpAcreedoraTotal = 0;
                  const hpAcreedoraRows: { code: string; name: string; amount: number }[] = [];

                  let otrosPasivosTotal = 0;
                  const otrosPasivosRows: { code: string; name: string; amount: number }[] = [];

                  let periodificacionesPasivoTotal = 0;   // subgrupo 48, lado acreedor
                  const periodificacionesPasivoRows: { code: string; name: string; amount: number }[] = [];

                  Object.entries(accountMap).forEach(([code, data]) => {
                    const debe = data.debe;
                    const haber = data.haber;
                    if (debe === 0 && haber === 0) return;

                    const saldoDeudorNeto = debe - haber;
                    const saldoAcreedorNeto = haber - debe;

                    // Grupo 2: Inmovilizado y Activo No Corriente — por subgrupo
                    if (code.startsWith('2')) {
                      if (code.startsWith('28') || code.startsWith('29')) {
                        // Amortizaciones acumuladas y deterioros: minoran el activo (saldo acreedor)
                        if (saldoAcreedorNeto !== 0) {
                          amortizacionAcumulada += saldoAcreedorNeto;
                          amortizacionRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                        }
                      } else if (saldoDeudorNeto !== 0) {
                        if (code.startsWith('20')) {
                          inmovilizadoIntangible += saldoDeudorNeto;
                          inmovilizadoIntangibleRows.push({ code, name: data.name, amount: saldoDeudorNeto });
                        } else if (code.startsWith('22')) {
                          inversionesInmobiliarias += saldoDeudorNeto;
                          inversionesInmobiliariasRows.push({ code, name: data.name, amount: saldoDeudorNeto });
                        } else if (code.startsWith('24') || code.startsWith('25') || code.startsWith('26')) {
                          inversionesFinancierasLP += saldoDeudorNeto;
                          inversionesFinancierasLPRows.push({ code, name: data.name, amount: saldoDeudorNeto });
                        } else {
                          // 21x (material) y 23x (en curso) van juntos en el epígrafe II
                          inmovilizadoMaterial += saldoDeudorNeto;
                          inmovilizadoMaterialRows.push({ code, name: data.name, amount: saldoDeudorNeto });
                        }
                      }
                    }
                    // Grupo 3: Existencias
                    else if (code.startsWith('3')) {
                      if (code.startsWith('39')) {
                        existenciasTotal -= saldoAcreedorNeto;
                        existenciasRows.push({ code, name: data.name, amount: -saldoAcreedorNeto });
                      } else {
                        existenciasTotal += saldoDeudorNeto;
                        existenciasRows.push({ code, name: data.name, amount: saldoDeudorNeto });
                      }
                    }
                    // Grupo 1: Patrimonio Neto y Pasivo No Corriente — por subgrupo
                    else if (code.startsWith('1')) {
                      if (code.startsWith('129')) {
                        // Regularización tratada en resultadoEjercicio
                        return;
                      }
                      // B) Pasivo No Corriente: provisiones (14x), deudas con empresas del
                      // grupo (16x) y deudas con entidades de crédito y otros (17x/18x) —
                      // tres líneas separadas, como exige la estructura oficial.
                      if (code.startsWith('14')) {
                        provisionesLPTotal += saldoAcreedorNeto;
                        provisionesLPRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                      } else if (code.startsWith('16')) {
                        deudasGrupoLPTotal += saldoAcreedorNeto;
                        deudasGrupoLPRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                      } else if (code.startsWith('17') || code.startsWith('18')) {
                        deudasEntidadesLPTotal += saldoAcreedorNeto;
                        deudasEntidadesLPRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                      }
                      // A) Patrimonio Neto
                      else if (code.startsWith('10')) {
                        capitalTotal += saldoAcreedorNeto;
                        capitalRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                      } else if (code.startsWith('11')) {
                        reservasTotal += saldoAcreedorNeto;
                        reservasRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                      } else if (code.startsWith('12')) {
                        resultadosAnterioresTotal += saldoAcreedorNeto;
                        resultadosAnterioresRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                      } else if (code.startsWith('130') || code.startsWith('131') || code.startsWith('132')) {
                        // A-3) Subvenciones, donaciones y legados recibidos
                        subvencionesTotal += saldoAcreedorNeto;
                        subvencionesRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                      } else if (code.startsWith('133') || code.startsWith('138')) {
                        // A-2) Ajustes por cambios de valor
                        ajustesCambioValorTotal += saldoAcreedorNeto;
                        ajustesCambioValorRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                      } else {
                        // Resto del grupo 1 (136/137/139, 19x...): otros fondos propios
                        otrosFondosPropiosTotal += saldoAcreedorNeto;
                        otrosFondosPropiosRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                      }
                    }
                    // Grupo 4: Deudores y Acreedores por operaciones comerciales
                    else if (code.startsWith('4')) {
                      if (code.startsWith('47')) {
                        // Cuentas con Hacienda y Seguridad Social
                        if (saldoDeudorNeto > 0) {
                          hpDeudoraTotal += saldoDeudorNeto;
                          hpDeudoraRows.push({ code, name: data.name, amount: saldoDeudorNeto });
                        } else if (saldoAcreedorNeto > 0) {
                          hpAcreedoraTotal += saldoAcreedorNeto;
                          hpAcreedoraRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                        }
                      } else if (code.startsWith('43') || code.startsWith('44') || code.startsWith('46')) {
                        // Clientes y deudores
                        if (saldoDeudorNeto >= 0) {
                          clientesTotal += saldoDeudorNeto;
                          clientesRows.push({ code, name: data.name, amount: saldoDeudorNeto });
                        } else {
                          proveedoresTotal += saldoAcreedorNeto;
                          proveedoresRows.push({ code, name: `${data.name} (Anticipo)`, amount: saldoAcreedorNeto });
                        }
                      } else if (code.startsWith('40') || code.startsWith('41')) {
                        // Proveedores y acreedores
                        if (saldoAcreedorNeto >= 0) {
                          proveedoresTotal += saldoAcreedorNeto;
                          proveedoresRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                        } else {
                          clientesTotal += saldoDeudorNeto;
                          clientesRows.push({ code, name: `${data.name} (Anticipo a favor)`, amount: saldoDeudorNeto });
                        }
                      } else if (code.startsWith('48')) {
                        // Ajustes por periodificación: línea propia, a un lado u otro
                        // según si el ajuste es un gasto anticipado (activo) o un ingreso
                        // anticipado (pasivo).
                        if (saldoDeudorNeto > 0) {
                          periodificacionesActivoTotal += saldoDeudorNeto;
                          periodificacionesActivoRows.push({ code, name: data.name, amount: saldoDeudorNeto });
                        } else if (saldoAcreedorNeto > 0) {
                          periodificacionesPasivoTotal += saldoAcreedorNeto;
                          periodificacionesPasivoRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                        }
                      } else {
                        // Otras cuentas deudor/acreedor (49x deterioro de créditos, etc.)
                        if (saldoDeudorNeto > 0) {
                          otrosActivosTotal += saldoDeudorNeto;
                          otrosActivosRows.push({ code, name: data.name, amount: saldoDeudorNeto });
                        } else if (saldoAcreedorNeto > 0) {
                          otrosPasivosTotal += saldoAcreedorNeto;
                          otrosPasivosRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                        }
                      }
                    }
                    // Grupo 5: Cuentas financieras y Tesorería
                    else if (code.startsWith('5')) {
                      if (code.startsWith('57')) {
                        // Caja y Bancos
                        tesoreriaTotal += saldoDeudorNeto;
                        tesoreriaRows.push({ code, name: data.name, amount: saldoDeudorNeto });
                      } else if (code.startsWith('51') || code.startsWith('52')) {
                        // Deudas a corto plazo
                        deudasCortoPlazoTotal += saldoAcreedorNeto;
                        deudasCortoPlazoRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                      } else if (code.startsWith('58')) {
                        // Activos no corrientes y grupos enajenables mantenidos para la venta
                        if (saldoDeudorNeto !== 0) {
                          activosMantenidosVentaTotal += saldoDeudorNeto;
                          activosMantenidosVentaRows.push({ code, name: data.name, amount: saldoDeudorNeto });
                        }
                      } else {
                        // Cuentas corrientes socios 55x, inversiones c/p 54x, fianzas 56x...
                        if (saldoDeudorNeto > 0) {
                          otrosActivosTotal += saldoDeudorNeto;
                          otrosActivosRows.push({ code, name: data.name, amount: saldoDeudorNeto });
                        } else if (saldoAcreedorNeto > 0) {
                          otrosPasivosTotal += saldoAcreedorNeto;
                          otrosPasivosRows.push({ code, name: data.name, amount: saldoAcreedorNeto });
                        }
                      }
                    }
                  });

                  // Totals
                  const inmovilizadoBruto = inmovilizadoIntangible + inmovilizadoMaterial + inversionesInmobiliarias + inversionesFinancierasLP;
                  const totalActivoNoCorriente = inmovilizadoBruto - amortizacionAcumulada;
                  const totalActivoCorriente = activosMantenidosVentaTotal + existenciasTotal + clientesTotal + hpDeudoraTotal + periodificacionesActivoTotal + tesoreriaTotal + otrosActivosTotal;
                  const totalActivo = totalActivoNoCorriente + totalActivoCorriente;

                  const fondosPropiosTotal = capitalTotal + reservasTotal + resultadosAnterioresTotal + otrosFondosPropiosTotal + resultadoEjercicio;
                  const totalPatrimonioNeto = fondosPropiosTotal + ajustesCambioValorTotal + subvencionesTotal;
                  const pasivoNoCorrienteTotal = provisionesLPTotal + deudasGrupoLPTotal + deudasEntidadesLPTotal;
                  const totalPasivoCorriente = proveedoresTotal + deudasCortoPlazoTotal + hpAcreedoraTotal + periodificacionesPasivoTotal + otrosPasivosTotal;
                  const totalPasivoPN = totalPatrimonioNeto + pasivoNoCorrienteTotal + totalPasivoCorriente;

                  const diferencia = Math.round((totalActivo - totalPasivoPN) * 100) / 100;
                  const descuadreAbs = Math.abs(diferencia);
                  const cuadra = descuadreAbs < 0.01;

                  return (
                    <div className="fs-balance-container">
                      <div className="fs-balance-header-card">
                        <div>
                          <span className="fs-comp-31">Balance de Situación Consolidado (PGC)</span>
                          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.7rem', opacity: 0.7 }}>
                            Valores consolidados a fecha {new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                        <button
                          onClick={() => showFeedback("Exportando Balance de Situación a formato oficial PDF...")}
                          className="fs-comp-82"
                        >
                          Exportar PDF/Borrador
                        </button>
                      </div>

                      {/* DOS COLUMNAS: ACTIVO (Izq) | PASIVO + PATRIMONIO NETO (Dcha) */}
                      <div className="fs-balance-grid">
                        
                        {/* ================= COLUMNA IZQUIERDA: ACTIVO ================= */}
                        <div className="fs-balance-column">
                          <div className="fs-balance-column-title">
                            <span>ACTIVO (ESTRUCTURA ECONÓMICA)</span>
                            <span className="fs-comp-amount fs-amount-positive">{formatCurrency(totalActivo)}</span>
                          </div>

                          {/* 1. ACTIVO NO CORRIENTE */}
                          <div className="fs-balance-section">
                            <div className="fs-balance-section-head">
                              <span>A) Activo No Corriente</span>
                              <span className="fs-comp-amount">{formatCurrency(totalActivoNoCorriente)}</span>
                            </div>

                            <div className="fs-balance-row">
                              <span>• I. Inmovilizado Intangible (20x)</span>
                              <span className="fs-comp-amount">{formatCurrency(inmovilizadoIntangible)}</span>
                            </div>
                            {inmovilizadoIntangibleRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            <div className="fs-balance-row">
                              <span>• II. Inmovilizado Material (21x/23x)</span>
                              <span className="fs-comp-amount">{formatCurrency(inmovilizadoMaterial)}</span>
                            </div>
                            {inmovilizadoMaterialRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            <div className="fs-balance-row">
                              <span>• III. Inversiones Inmobiliarias (22x)</span>
                              <span className="fs-comp-amount">{formatCurrency(inversionesInmobiliarias)}</span>
                            </div>
                            {inversionesInmobiliariasRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            <div className="fs-balance-row">
                              <span>• IV/V. Inversiones Financieras a Largo Plazo (24x/25x/26x)</span>
                              <span className="fs-comp-amount">{formatCurrency(inversionesFinancierasLP)}</span>
                            </div>
                            {inversionesFinancierasLPRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            <div className="fs-balance-row">
                              <span style={{ color: amortizacionAcumulada > 0 ? '#fb7185' : 'inherit' }}>
                                • (-) Amortización Acumulada y Deterioros (28x/29x)
                              </span>
                              <span className="fs-comp-amount" style={{ color: amortizacionAcumulada > 0 ? '#fb7185' : 'inherit' }}>
                                {amortizacionAcumulada > 0 ? `-${formatCurrency(amortizacionAcumulada)}` : '0,00 €'}
                              </span>
                            </div>
                            {amortizacionRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span style={{ color: '#fb7185' }}>-{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            <div className="fs-balance-section-total">
                              <span>Total Activo No Corriente:</span>
                              <span className="fs-comp-amount">{formatCurrency(totalActivoNoCorriente)}</span>
                            </div>
                          </div>

                          {/* 2. ACTIVO CORRIENTE */}
                          <div className="fs-balance-section">
                            <div className="fs-balance-section-head">
                              <span>B) Activo Corriente</span>
                              <span className="fs-comp-amount">{formatCurrency(totalActivoCorriente)}</span>
                            </div>

                            {/* Activos mantenidos para la venta (subgrupo 58) — epígrafe I
                                oficial, se omite cuando no hay ninguna cuenta de este tipo. */}
                            {activosMantenidosVentaTotal !== 0 && (
                              <>
                                <div className="fs-balance-row">
                                  <span>• I. Activos No Corrientes Mantenidos para la Venta (58x)</span>
                                  <span className="fs-comp-amount">{formatCurrency(activosMantenidosVentaTotal)}</span>
                                </div>
                                {activosMantenidosVentaRows.map(r => (
                                  <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                    <span>{r.code} — {r.name}</span>
                                    <span>{formatCurrency(r.amount)}</span>
                                  </div>
                                ))}
                              </>
                            )}

                            {/* Existencias */}
                            <div className="fs-balance-row">
                              <span>• II. Existencias comerciales (Grupo 3)</span>
                              <span className="fs-comp-amount">{formatCurrency(existenciasTotal)}</span>
                            </div>
                            {existenciasRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            {/* Clientes */}
                            <div className="fs-balance-row">
                              <span>• III. Deudores comerciales y Clientes (43x/44x/46x)</span>
                              <span className="fs-comp-amount">{formatCurrency(clientesTotal)}</span>
                            </div>
                            {clientesRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            {/* Hacienda Pública Deudora */}
                            <div className="fs-balance-row">
                              <span>• Hacienda Pública Deudora (IVA soportado / IRPF 47x)</span>
                              <span className="fs-comp-amount">{formatCurrency(hpDeudoraTotal)}</span>
                            </div>
                            {hpDeudoraRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            {/* Periodificaciones a corto plazo (subgrupo 48, lado deudor) */}
                            {periodificacionesActivoTotal !== 0 && (
                              <>
                                <div className="fs-balance-row">
                                  <span>• VI. Periodificaciones a Corto Plazo (48x)</span>
                                  <span className="fs-comp-amount">{formatCurrency(periodificacionesActivoTotal)}</span>
                                </div>
                                {periodificacionesActivoRows.map(r => (
                                  <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                    <span>{r.code} — {r.name}</span>
                                    <span>{formatCurrency(r.amount)}</span>
                                  </div>
                                ))}
                              </>
                            )}

                            {/* Efectivo y Tesorería */}
                            <div className="fs-balance-row">
                              <span>• VII. Efectivo y otros activos líquidos (57x Tesorería)</span>
                              <span className="fs-comp-amount">{formatCurrency(tesoreriaTotal)}</span>
                            </div>
                            {tesoreriaRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            {/* Otros activos corrientes */}
                            {otrosActivosTotal !== 0 && (
                              <>
                                <div className="fs-balance-row">
                                  <span>• Otros activos e inversiones corrientes (54x/55x/49x)</span>
                                  <span className="fs-comp-amount">{formatCurrency(otrosActivosTotal)}</span>
                                </div>
                                {otrosActivosRows.map(r => (
                                  <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                    <span>{r.code} — {r.name}</span>
                                    <span>{formatCurrency(r.amount)}</span>
                                  </div>
                                ))}
                              </>
                            )}

                            <div className="fs-balance-section-total">
                              <span>Total Activo Corriente:</span>
                              <span className="fs-comp-amount">{formatCurrency(totalActivoCorriente)}</span>
                            </div>
                          </div>

                          {/* TOTAL ACTIVO */}
                          <div className="fs-balance-grand-total">
                            <span>TOTAL ACTIVO (A + B)</span>
                            <span className="fs-comp-amount fs-amount-positive" style={{ fontSize: '1rem' }}>
                              {formatCurrency(totalActivo)}
                            </span>
                          </div>
                        </div>

                        {/* ================= COLUMNA DERECHA: PASIVO + PATRIMONIO NETO ================= */}
                        <div className="fs-balance-column">
                          <div className="fs-balance-column-title">
                            <span>PASIVO Y PATRIMONIO NETO (FINANCIACIÓN)</span>
                            <span className="fs-comp-amount fs-amount-positive">{formatCurrency(totalPasivoPN)}</span>
                          </div>

                          {/* 1. PATRIMONIO NETO */}
                          <div className="fs-balance-section">
                            <div className="fs-balance-section-head">
                              <span>A) Patrimonio Neto</span>
                              <span className="fs-comp-amount">{formatCurrency(totalPatrimonioNeto)}</span>
                            </div>

                            {/* A-1) Fondos Propios */}
                            <div className="fs-balance-row" style={{ fontWeight: 600 }}>
                              <span>A-1) Fondos Propios</span>
                              <span className="fs-comp-amount">{formatCurrency(fondosPropiosTotal)}</span>
                            </div>

                            <div className="fs-balance-row fs-balance-row-sub">
                              <span>• I. Capital (10x)</span>
                              <span>{formatCurrency(capitalTotal)}</span>
                            </div>
                            {capitalRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span style={{ paddingLeft: '0.75rem' }}>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            <div className="fs-balance-row fs-balance-row-sub">
                              <span>• III. Reservas (11x)</span>
                              <span>{formatCurrency(reservasTotal)}</span>
                            </div>
                            {reservasRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span style={{ paddingLeft: '0.75rem' }}>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            <div className="fs-balance-row fs-balance-row-sub">
                              <span>• V. Resultados de Ejercicios Anteriores (12x, excl. 129)</span>
                              <span>{formatCurrency(resultadosAnterioresTotal)}</span>
                            </div>
                            {resultadosAnterioresRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span style={{ paddingLeft: '0.75rem' }}>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            {otrosFondosPropiosTotal !== 0 && (
                              <>
                                <div className="fs-balance-row fs-balance-row-sub">
                                  <span>• IX. Otros Instrumentos de Patrimonio Neto (136x/137x/139x/19x)</span>
                                  <span>{formatCurrency(otrosFondosPropiosTotal)}</span>
                                </div>
                                {otrosFondosPropiosRows.map(r => (
                                  <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                    <span style={{ paddingLeft: '0.75rem' }}>{r.code} — {r.name}</span>
                                    <span>{formatCurrency(r.amount)}</span>
                                  </div>
                                ))}
                              </>
                            )}

                            <div className="fs-balance-row fs-balance-row-sub" style={{ fontWeight: 600 }}>
                              <span>• VII. Resultado del Ejercicio (P&G del periodo / 129000)</span>
                              <span className={resultadoEjercicio >= 0 ? 'fs-amount-positive' : 'fs-amount-negative'}>
                                {resultadoEjercicio >= 0 ? `+${formatCurrency(resultadoEjercicio)}` : formatCurrency(resultadoEjercicio)}
                              </span>
                            </div>
                            <div className="fs-balance-row fs-balance-row-sub">
                              <span style={{ paddingLeft: '0.75rem' }}>Ingresos Grupo 7 (+{formatCurrency(totalIngresos7)}) − Gastos Grupo 6 (-{formatCurrency(totalGastos6)}){saldo129 !== 0 ? ` + Asiento 129 (${formatCurrency(saldo129)})` : ''}</span>
                              <span className={resultadoEjercicio >= 0 ? 'fs-amount-positive' : 'fs-amount-negative'}>
                                {formatCurrency(resultadoEjercicio)}
                              </span>
                            </div>

                            {/* A-2) Ajustes por cambios de valor */}
                            <div className="fs-balance-row" style={{ fontWeight: 600 }}>
                              <span>A-2) Ajustes por Cambios de Valor (133x/138x)</span>
                              <span className="fs-comp-amount">{formatCurrency(ajustesCambioValorTotal)}</span>
                            </div>
                            {ajustesCambioValorRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            {/* A-3) Subvenciones, donaciones y legados recibidos */}
                            <div className="fs-balance-row" style={{ fontWeight: 600 }}>
                              <span>A-3) Subvenciones, Donaciones y Legados Recibidos (130x/131x/132x)</span>
                              <span className="fs-comp-amount">{formatCurrency(subvencionesTotal)}</span>
                            </div>
                            {subvencionesRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            <div className="fs-balance-section-total">
                              <span>Total Patrimonio Neto:</span>
                              <span className="fs-comp-amount">{formatCurrency(totalPatrimonioNeto)}</span>
                            </div>
                          </div>

                          {/* 2. PASIVO NO CORRIENTE */}
                          <div className="fs-balance-section">
                            <div className="fs-balance-section-head">
                              <span>B) Pasivo No Corriente (Largo Plazo)</span>
                              <span className="fs-comp-amount">{formatCurrency(pasivoNoCorrienteTotal)}</span>
                            </div>

                            <div className="fs-balance-row">
                              <span>• I. Provisiones a Largo Plazo (14x)</span>
                              <span className="fs-comp-amount">{formatCurrency(provisionesLPTotal)}</span>
                            </div>
                            {provisionesLPRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            <div className="fs-balance-row">
                              <span>• II. Deudas a Largo Plazo con Empresas del Grupo y Asociadas (16x)</span>
                              <span className="fs-comp-amount">{formatCurrency(deudasGrupoLPTotal)}</span>
                            </div>
                            {deudasGrupoLPRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            <div className="fs-balance-row">
                              <span>• III. Deudas a Largo Plazo con Entidades de Crédito y Otros (17x/18x)</span>
                              <span className="fs-comp-amount">{formatCurrency(deudasEntidadesLPTotal)}</span>
                            </div>
                            {deudasEntidadesLPRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            <div className="fs-balance-section-total">
                              <span>Total Pasivo No Corriente:</span>
                              <span className="fs-comp-amount">{formatCurrency(pasivoNoCorrienteTotal)}</span>
                            </div>
                          </div>

                          {/* 3. PASIVO CORRIENTE */}
                          <div className="fs-balance-section">
                            <div className="fs-balance-section-head">
                              <span>C) Pasivo Corriente (Corto Plazo)</span>
                              <span className="fs-comp-amount">{formatCurrency(totalPasivoCorriente)}</span>
                            </div>

                            {/* Proveedores */}
                            <div className="fs-balance-row">
                              <span>• I. Acreedores comerciales y Proveedores (40x/41x)</span>
                              <span className="fs-comp-amount">{formatCurrency(proveedoresTotal)}</span>
                            </div>
                            {proveedoresRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            {/* Deudas a Corto Plazo */}
                            <div className="fs-balance-row">
                              <span>• II. Deudas a corto plazo (51x/52x)</span>
                              <span className="fs-comp-amount">{formatCurrency(deudasCortoPlazoTotal)}</span>
                            </div>
                            {deudasCortoPlazoRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            {/* Hacienda Pública Acreedora */}
                            <div className="fs-balance-row">
                              <span>• III. Hacienda Pública Acreedora (IVA repercutido / IRPF / SS 47x)</span>
                              <span className="fs-comp-amount">{formatCurrency(hpAcreedoraTotal)}</span>
                            </div>
                            {hpAcreedoraRows.map(r => (
                              <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                <span>{r.code} — {r.name}</span>
                                <span>{formatCurrency(r.amount)}</span>
                              </div>
                            ))}

                            {/* Periodificaciones a corto plazo (subgrupo 48, lado acreedor) */}
                            {periodificacionesPasivoTotal !== 0 && (
                              <>
                                <div className="fs-balance-row">
                                  <span>• IV. Periodificaciones a Corto Plazo (48x)</span>
                                  <span className="fs-comp-amount">{formatCurrency(periodificacionesPasivoTotal)}</span>
                                </div>
                                {periodificacionesPasivoRows.map(r => (
                                  <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                    <span>{r.code} — {r.name}</span>
                                    <span>{formatCurrency(r.amount)}</span>
                                  </div>
                                ))}
                              </>
                            )}

                            {/* Otros pasivos corrientes */}
                            {otrosPasivosTotal !== 0 && (
                              <>
                                <div className="fs-balance-row">
                                  <span>• Otros pasivos corrientes (55x)</span>
                                  <span className="fs-comp-amount">{formatCurrency(otrosPasivosTotal)}</span>
                                </div>
                                {otrosPasivosRows.map(r => (
                                  <div key={r.code} className="fs-balance-row fs-balance-row-sub">
                                    <span>{r.code} — {r.name}</span>
                                    <span>{formatCurrency(r.amount)}</span>
                                  </div>
                                ))}
                              </>
                            )}

                            <div className="fs-balance-section-total">
                              <span>Total Pasivo Corriente:</span>
                              <span className="fs-comp-amount">{formatCurrency(totalPasivoCorriente)}</span>
                            </div>
                          </div>

                          {/* TOTAL PASIVO + PATRIMONIO NETO */}
                          <div className="fs-balance-grand-total">
                            <span>TOTAL PASIVO + PN (A + B + C)</span>
                            <span className="fs-comp-amount fs-amount-positive" style={{ fontSize: '1rem' }}>
                              {formatCurrency(totalPasivoPN)}
                            </span>
                          </div>
                        </div>

                      </div>

                      {/* ================= BARRA DE COHERENCIA CONTABLE ================= */}
                      <div className={`fs-balance-audit-bar ${cuadra ? 'cuadra' : 'descuadre'}`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          {cuadra ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <ShieldAlert className="w-5 h-5 text-rose-400" />
                          )}
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '0.8rem' }}>
                              {cuadra
                                ? `CUADRA — Identidad Patrimonial Verificada: Total Activo (${formatCurrency(totalActivo)}) = Total Pasivo + PN (${formatCurrency(totalPasivoPN)})`
                                : `DESCUADRE CONTABLE DETECTADO: Diferencia de ${formatCurrency(descuadreAbs)} (Total Activo: ${formatCurrency(totalActivo)} vs Total Pasivo+PN: ${formatCurrency(totalPasivoPN)})`}
                            </div>
                            <div style={{ opacity: 0.85, fontSize: '0.7rem', marginTop: '2px' }}>
                              {cuadra
                                ? 'La partida doble se cumple rigurosamente en todos los asientos contables contabilizados.'
                                : 'Existe un desajuste en los asientos del Libro Diario o una cuenta sin balancear que rompe la ecuación fundamental.'}
                            </div>
                          </div>
                        </div>

                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.8 }}>
                            Diferencia Neta:{' '}
                          </span>
                          <strong className="fs-comp-amount" style={{ fontSize: '0.85rem' }}>
                            {formatCurrency(descuadreAbs)}
                          </strong>
                        </div>
                      </div>

                    </div>
                  );
                })()}
              </div>
            )}

            {/* ========================================================= */}
            {/* 4. PÉRDIDAS Y GANANCIAS (Income Statement) */}
            {/* ========================================================= */}
            {activeMenu === 'pyg' && (
              <div className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Cálculo y desglose del <span className="fs-comp-24">Resultado de Explotación, Financiero y de Impuestos</span> del ejercicio en curso. Evaluando las cuentas de Compras y Gastos (Grupo 6) e Ingresos y Ventas (Grupo 7).
                  </p>
                </div>

                {(() => {
                  // Estructura del Modelo Normal de Pérdidas y Ganancias (RD 1514/2007,
                  // Tercera Parte). Cada partida numerada corresponde a su epígrafe
                  // oficial; se agrupa por subgrupo (2 primeros dígitos del código de
                  // cuenta) para que funcione igual con un PGC de 3 o de 6 dígitos, y
                  // el desglose sale de las cuentas realmente usadas en el diario, no
                  // de una lista fija.
                  type Bucket = { total: number; rows: Map<string, number> };
                  const newBucket = (): Bucket => ({ total: 0, rows: new Map() });

                  const bIngresosVentas    = newBucket(); // 70 — cifra de negocios
                  const bVariacionExist    = newBucket(); // 71 — variación existencias PT/curso
                  const bTrabajosActivo    = newBucket(); // 73 — trabajos para el propio activo
                  const bAprovisionam      = newBucket(); // 60/61 — compras y variación existencias comerciales
                  const bOtrosIngresosExpl = newBucket(); // 74/75 — subvenciones de explotación y otros ingresos de gestión
                  const bGastosPersonal    = newBucket(); // 64 — sueldos, salarios y seg. social a cargo de la empresa
                  const bOtrosGastosExpl   = newBucket(); // 62/65 + 63x que no sea impuesto sobre beneficios — servicios ext., tributos, otros gastos gestión
                  const bAmortizaciones    = newBucket(); // 68
                  const bOtrosResultExpl   = newBucket(); // 67/77/79 — deterioro, enajenaciones y excesos de provisión de explotación
                  const bIngresosFinanc    = newBucket(); // 76, excepto 768 (partida 12)
                  const bGastosFinanc      = newBucket(); // 66, excepto 668/663/766 y 666 (partida 13)
                  const bValorRazonable    = newBucket(); // 663/763 — partida 14, Variación de Valor Razonable
                  const bDiferenciasCambio = newBucket(); // 668/768 — partida 15, Diferencias de Cambio
                  const bImpuestoBeneficio = newBucket(); // 630/633/638 — Impuesto sobre beneficios (no el resto del 63)

                  const push = (b: Bucket, code: string, amount: number) => {
                    b.total += amount;
                    b.rows.set(code, (b.rows.get(code) || 0) + amount);
                  };

                  // Balance en bruto (debe/haber, sin categorizar) de cada cuenta de grupo
                  // 6 o 7 que aparezca en el diario. El botón "Generar Asiento de
                  // Regularización" lo usa para cerrar TODAS las cuentas de resultados
                  // reales, no solo las 3 que asumía antes — así sigue funcionando aunque
                  // aparezcan cuentas de personal, financieras, etc.
                  const g67Balances = new Map<string, { name: string; debe: number; haber: number }>();

                  entries.forEach(e => {
                    e.lines.forEach(l => {
                      const code = String(l.account || '');
                      const sub2 = code.slice(0, 2);
                      const sub3 = code.slice(0, 3);
                      const ingreso = l.credit - l.debit;
                      const gasto = l.debit - l.credit;

                      if (sub2.startsWith('6') || sub2.startsWith('7')) {
                        const existing = g67Balances.get(code) || {
                          name: chartOfAccounts.find(c => c.code === code)?.name || `Cuenta ${code}`,
                          debe: 0,
                          haber: 0
                        };
                        existing.debe += l.debit;
                        existing.haber += l.credit;
                        g67Balances.set(code, existing);
                      }

                      if (sub2 === '70') push(bIngresosVentas, code, ingreso);
                      else if (sub2 === '71') push(bVariacionExist, code, ingreso);
                      else if (sub2 === '73') push(bTrabajosActivo, code, ingreso);
                      else if (sub2 === '60' || sub2 === '61') push(bAprovisionam, code, gasto);
                      else if (sub2 === '74' || sub2 === '75') push(bOtrosIngresosExpl, code, ingreso);
                      else if (sub2 === '64') push(bGastosPersonal, code, gasto);
                      else if (sub2 === '62' || sub2 === '65') push(bOtrosGastosExpl, code, gasto);
                      // 630/633/638 = Impuesto sobre beneficios (partida 17, va bajo el
                      // resultado financiero); el resto del subgrupo 63 (631/634/636,
                      // otros tributos) es gasto de explotación corriente.
                      else if (sub3 === '630' || sub3 === '633' || sub3 === '638') push(bImpuestoBeneficio, code, gasto);
                      else if (sub2 === '63') push(bOtrosGastosExpl, code, gasto);
                      else if (sub2 === '68') push(bAmortizaciones, code, gasto);
                      // 67x (deterioro/pérdidas, naturaleza deudora) resta y 77x/79x
                      // (reversiones, excesos de provisión, naturaleza acreedora) suma:
                      // credit - debit ya da el signo correcto para ambos casos.
                      else if (sub2 === '67' || sub2 === '77' || sub2 === '79') push(bOtrosResultExpl, code, l.credit - l.debit);
                      // 668/768: Diferencias de Cambio (partida 15). 663/763: Variación de
                      // Valor Razonable en instrumentos financieros (partida 14). El resto
                      // de 66/76 es el ingreso/gasto financiero "normal" (partidas 12/13).
                      else if (sub3 === '668' || sub3 === '768') push(bDiferenciasCambio, code, l.credit - l.debit);
                      else if (sub3 === '663' || sub3 === '763') push(bValorRazonable, code, l.credit - l.debit);
                      else if (sub2 === '76') push(bIngresosFinanc, code, ingreso);
                      else if (sub2 === '66') push(bGastosFinanc, code, gasto);
                    });
                  });

                  const accountName = (code: string) =>
                    chartOfAccounts.find(c => c.code === code)?.name || `Cuenta ${code}`;

                  const detail = (b: Bucket) =>
                    Array.from(b.rows.entries())
                      .filter(([, amount]) => Math.abs(amount) > 0.005)
                      .sort((a, b2) => a[0].localeCompare(b2[0]));

                  const importeCifraNegocios = bIngresosVentas.total;
                  const variacionExistencias = bVariacionExist.total;
                  const trabajosActivo = bTrabajosActivo.total;
                  const aprovisionamientos = bAprovisionam.total;
                  const otrosIngresosExplotacion = bOtrosIngresosExpl.total;
                  const gastosPersonal = bGastosPersonal.total;
                  const otrosGastosExplotacion = bOtrosGastosExpl.total;
                  const amortizaciones = bAmortizaciones.total;
                  const otrosResultadosExplotacion = bOtrosResultExpl.total;

                  const resultadoExplotacion =
                    importeCifraNegocios + variacionExistencias + trabajosActivo - aprovisionamientos
                    + otrosIngresosExplotacion - gastosPersonal - otrosGastosExplotacion - amortizaciones
                    + otrosResultadosExplotacion;

                  const ingresosFinancieros = bIngresosFinanc.total;
                  const gastosFinancieros = bGastosFinanc.total;
                  const variacionValorRazonable = bValorRazonable.total;
                  const diferenciasCambio = bDiferenciasCambio.total;
                  const resultadoFinanciero = ingresosFinancieros - gastosFinancieros + variacionValorRazonable + diferenciasCambio;

                  const resultadoAntesImpuestos = resultadoExplotacion + resultadoFinanciero;
                  const impuestoBeneficios = bImpuestoBeneficio.total;
                  const resultadoEjercicio = resultadoAntesImpuestos - impuestoBeneficios;

                  // Compat con el asiento de regularización de más abajo, que ya
                  // usaba estos tres nombres.
                  const comprasConsumos = aprovisionamientos;
                  const serviciosExteriores = otrosGastosExplotacion;

                  const Linea = ({ label, amount, bucket, sign }: { label: string; amount: number; bucket: Bucket; sign?: 1 | -1 }) => (
                    <>
                      <div className="fs-comp-97">
                        <span>{label}</span>
                        <span className="fs-comp-101">{amount === 0 ? formatCurrency(0) : `${(sign ?? 1) * amount >= 0 ? '+' : '-'}${formatCurrency(Math.abs(amount))}`}</span>
                      </div>
                      {detail(bucket).map(([code, amt]) => (
                        <div key={code} className="fs-comp-99">
                          <span>• {accountName(code)} ({code})</span>
                          <span>{formatCurrency(amt)}</span>
                        </div>
                      ))}
                    </>
                  );

                  return (
                    <div className="fs-comp-26">
                      <div className="fs-comp-93">
                        <div className="fs-comp-94">
                          <span>CUENTA DE PÉRDIDAS Y GANANCIAS — MODELO NORMAL (RD 1514/2007)</span>
                          <span className="fs-comp-95">EJERCICIO {new Date().getFullYear()}</span>
                        </div>

                        <div className="fs-comp-96">
                          <Linea label="1. Importe neto de la cifra de negocios" amount={importeCifraNegocios} bucket={bIngresosVentas} />
                          <Linea label="2. Variación de existencias de productos terminados y en curso" amount={variacionExistencias} bucket={bVariacionExist} />
                          <Linea label="3. Trabajos realizados por la empresa para su activo" amount={trabajosActivo} bucket={bTrabajosActivo} />
                          <Linea label="4. Aprovisionamientos" amount={-aprovisionamientos} bucket={bAprovisionam} />
                          <Linea label="5. Otros ingresos de explotación" amount={otrosIngresosExplotacion} bucket={bOtrosIngresosExpl} />
                          <Linea label="6. Gastos de personal" amount={-gastosPersonal} bucket={bGastosPersonal} />
                          <Linea label="7. Otros gastos de explotación" amount={-otrosGastosExplotacion} bucket={bOtrosGastosExpl} />
                          <Linea label="8. Amortización del inmovilizado" amount={-amortizaciones} bucket={bAmortizaciones} />
                          <Linea label="9-11. Otros resultados de explotación (deterioro, enajenaciones, excesos de provisión)" amount={otrosResultadosExplotacion} bucket={bOtrosResultExpl} />

                          <div className="fs-comp-102">
                            <span>A.1) RESULTADO DE EXPLOTACIÓN</span>
                            <span className={resultadoExplotacion >= 0 ? "text-[var(--color-brand-green)]" : "text-rose-500"}>
                              {formatCurrency(resultadoExplotacion)}
                            </span>
                          </div>

                          <Linea label="12. Ingresos financieros" amount={ingresosFinancieros} bucket={bIngresosFinanc} />
                          <Linea label="13. Gastos financieros" amount={-gastosFinancieros} bucket={bGastosFinanc} />
                          <Linea label="14. Variación de valor razonable en instrumentos financieros" amount={variacionValorRazonable} bucket={bValorRazonable} />
                          <Linea label="15. Diferencias de cambio" amount={diferenciasCambio} bucket={bDiferenciasCambio} />

                          <div className="fs-comp-103">
                            <span>A.2) RESULTADO FINANCIERO</span>
                            <span className={resultadoFinanciero >= 0 ? "text-emerald-400" : "text-rose-500"}>
                              {formatCurrency(resultadoFinanciero)}
                            </span>
                          </div>

                          <div className="fs-comp-104">
                            <span className="fs-comp-105">A.3) RESULTADO ANTES DE IMPUESTOS</span>
                            <span className={resultadoAntesImpuestos >= 0 ? "text-[var(--color-brand-green)]" : "text-rose-500"}>
                              {formatCurrency(resultadoAntesImpuestos)}
                            </span>
                          </div>

                          <Linea label="17. Impuesto sobre beneficios" amount={-impuestoBeneficios} bucket={bImpuestoBeneficio} />

                          <div className="fs-comp-104">
                            <span className="fs-comp-105">A.4) RESULTADO DEL EJERCICIO</span>
                            <span className={resultadoEjercicio >= 0 ? "text-[var(--color-brand-green)]" : "text-rose-500"}>
                              {formatCurrency(resultadoEjercicio)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="fs-comp-106">
                        <button
                          onClick={async () => {
                            // Cierra TODAS las cuentas de grupo 6 y 7 que tengan saldo real
                            // en el diario (no solo Ventas/Compras/Suministros como antes):
                            // cada cuenta de ingreso (7, naturaleza acreedora) se debita por
                            // su saldo, cada cuenta de gasto (6, naturaleza deudora) se
                            // abona por el suyo, y la diferencia neta va a la cuenta 129.
                            const lines: JournalLine[] = [];
                            let debitTotal = 0;
                            let creditTotal = 0;

                            g67Balances.forEach((data, code) => {
                              const isIncome = code.startsWith('7');
                              const net = isIncome ? data.haber - data.debe : data.debe - data.haber;
                              if (Math.abs(net) < 0.005) return;
                              if (net > 0) {
                                // Cierra una cuenta con su saldo natural (ingreso acreedor o
                                // gasto deudor): al ingreso se le hace un debe, al gasto un haber.
                                if (isIncome) { lines.push({ account: code, accountName: data.name, debit: net, credit: 0 }); debitTotal += net; }
                                else { lines.push({ account: code, accountName: data.name, debit: 0, credit: net }); creditTotal += net; }
                              } else {
                                // Saldo en sentido contrario al habitual (p.ej. un rappel o una
                                // cuenta contra-gasto): se cierra al revés.
                                if (isIncome) { lines.push({ account: code, accountName: data.name, debit: 0, credit: -net }); creditTotal += -net; }
                                else { lines.push({ account: code, accountName: data.name, debit: -net, credit: 0 }); debitTotal += -net; }
                              }
                            });

                            if (lines.length === 0) {
                              showFeedback('No hay cuentas de grupo 6 o 7 con saldo que regularizar.', true);
                              return;
                            }

                            const netResultado = debitTotal - creditTotal;
                            const cuenta129 = chartOfAccounts.find(c => c.code.startsWith('129'))?.code || '129000';
                            if (netResultado > 0) {
                              // Beneficio: 129 se abona (aumenta el Patrimonio Neto)
                              lines.push({ account: cuenta129, accountName: 'Resultado del Ejercicio (Pérdidas y Ganancias)', debit: 0, credit: netResultado });
                            } else if (netResultado < 0) {
                              // Pérdida: 129 se debita
                              lines.push({ account: cuenta129, accountName: 'Resultado del Ejercicio (Pérdidas y Ganancias)', debit: -netResultado, credit: 0 });
                            }

                            const ok = await addJournalEntry("Regularización contable de ingresos y gastos (Grupo 6 y 7)", lines, 'Regularización');
                            if (ok) setActiveMenu('review_entries');
                          }}
                          className="fs-comp-107"
                        >
                          Generar Asiento de Regularización (Cierre Grupo 6 y 7)
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ========================================================= */}
            {/* 5. LIBRO MAYOR (General Ledger search and view) */}
            {/* ========================================================= */}
            {activeMenu === 'mayor' && (
              <div className="fs-comp-21">
                {(() => {
                  const selectedAcc = selectedMayorAcc;
                  const setSelectedAcc = setSelectedMayorAcc;
                  
                  // Filter lines matching selection
                  const matchingLines: { date: string, concept: string, entryId: string, debit: number, credit: number }[] = [];
                  entries.forEach(e => {
                    e.lines.forEach(l => {
                      if (l.account === selectedAcc) {
                        matchingLines.push({
                          date: e.date,
                          concept: e.concept,
                          entryId: e.id,
                          debit: l.debit,
                          credit: l.credit
                        });
                      }
                    });
                  });

                  // Calculate running balance based on account nature (Asset/Expense increases on debit, Liabilities/Equity/Income on credit)
                  const isAssetOrExpense = selectedAcc.startsWith('2') || selectedAcc.startsWith('3') || selectedAcc.startsWith('43') || selectedAcc.startsWith('57') || selectedAcc.startsWith('6');
                  
                  let balance = 0;
                  const ledgerRows = matchingLines.map(line => {
                    if (isAssetOrExpense) {
                      balance += line.debit - line.credit;
                    } else {
                      balance += line.credit - line.debit;
                    }
                    return { ...line, runningBalance: balance };
                  });

                  return (
                    <div className="fs-comp-26">
                      <div className="fs-comp-108">
                        <div className="fs-comp-7">
                          <span className="fs-comp-109">Seleccione Cuenta Contable</span>
                          <select
                            value={selectedAcc}
                            onChange={(e) => setSelectedAcc(e.target.value)}
                            className="fs-comp-110"
                          >
                            {postableAccounts.map(c => (
                              <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="fs-comp-111">
                          <span className="fs-comp-109">Saldo Contable Acumulado</span>
                          <h4 className="fs-comp-112">{formatCurrency(balance)}</h4>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowNewAccountForm(v => !v)}
                          className="fs-comp-107"
                        >
                          {showNewAccountForm ? 'Cancelar' : '+ Nueva Cuenta'}
                        </button>
                      </div>

                      {showNewAccountForm && (
                        <form
                          onSubmit={async (e: any) => {
                            e.preventDefault();
                            const code = e.target.newCode.value.trim();
                            const name = e.target.newName.value.trim();
                            const level = e.target.newLevel.value;
                            const parentAccountId = e.target.newParent.value || undefined;
                            if (!code || !name) {
                              showFeedback('Código y nombre son obligatorios.', true);
                              return;
                            }

                            // Reglas del PGC: el nivel fija la longitud del código y cada
                            // cuenta cuelga del nivel inmediatamente superior, cuyo código
                            // es el comienzo del suyo (43 -> 430 -> 4300001).
                            const lengthOk: Record<string, boolean> = {
                              group: code.length === 1,
                              subgroup: code.length === 2,
                              account: code.length === 3,
                              subaccount: code.length >= 4
                            };
                            const lengthMsg: Record<string, string> = {
                              group: 'Un grupo tiene 1 dígito (1-9).',
                              subgroup: 'Un subgrupo tiene 2 dígitos (p. ej. 43).',
                              account: 'Una cuenta tiene 3 dígitos (p. ej. 430).',
                              subaccount: 'Una subcuenta tiene 4 o más dígitos y amplía el código de su cuenta (p. ej. 4300001).'
                            };
                            if (!lengthOk[level]) {
                              showFeedback(`Código no válido para el nivel elegido. ${lengthMsg[level]}`, true);
                              return;
                            }
                            if (chartOfAccounts.some(c => c.code === code)) {
                              showFeedback(`Ya existe una cuenta con el código ${code}.`, true);
                              return;
                            }

                            let parentId: string | undefined = undefined;
                            if (level !== 'group') {
                              const parentLevel: Record<string, string> = { subgroup: 'group', account: 'subgroup', subaccount: 'account' };
                              const parentLen: Record<string, number> = { subgroup: 1, account: 2, subaccount: 3 };
                              const chosen = parentAccountId ? chartOfAccounts.find(c => c.id === parentAccountId) : undefined;
                              // Sin padre elegido se deduce del código: 43 -> grupo 4, 430 -> subgrupo 43, 4300001 -> cuenta 430.
                              const parent = chosen || chartOfAccounts.find(c => c.code === code.slice(0, parentLen[level]) && c.level === parentLevel[level]);
                              if (!parent || !parent.id) {
                                showFeedback(`Falta la cuenta padre ${code.slice(0, parentLen[level])} (${parentLevel[level] === 'group' ? 'grupo' : parentLevel[level] === 'subgroup' ? 'subgrupo' : 'cuenta'}) en el plan: créala antes.`, true);
                                return;
                              }
                              if (parent.level && parent.level !== parentLevel[level]) {
                                showFeedback(`La cuenta padre ${parent.code} no es válida: debe ser un ${parentLevel[level] === 'group' ? 'grupo' : parentLevel[level] === 'subgroup' ? 'subgrupo' : 'cuenta'}.`, true);
                                return;
                              }
                              if (!code.startsWith(parent.code) || code === parent.code) {
                                showFeedback(`El código ${code} debe empezar por el de su cuenta padre (${parent.code}).`, true);
                                return;
                              }
                              parentId = parent.id;
                            }

                            const ok = await createChartOfAccount(code, name, level, parentId);
                            if (ok) {
                              e.target.reset();
                              setShowNewAccountForm(false);
                            }
                          }}
                          className="fs-comp-22"
                        >
                          <div className="fs-comp-138">
                            <div className="fs-comp-59">
                              <label className="fs-comp-60">Código PGC</label>
                              <input name="newCode" required placeholder="EJ: 629 (cuenta), 6291 (subcuenta)" type="text" pattern="[0-9]+" title="Solo dígitos (0-9)" className="fs-comp-127" />
                            </div>
                            <div className="fs-comp-59">
                              <label className="fs-comp-60">Denominación</label>
                              <input name="newName" required placeholder="EJ: Gastos de mensajería" type="text" className="fs-comp-127" />
                            </div>
                            <div className="fs-comp-59">
                              <label className="fs-comp-60">Nivel</label>
                              <select name="newLevel" defaultValue="account" className="fs-comp-63">
                                <option value="group">Grupo</option>
                                <option value="subgroup">Subgrupo</option>
                                <option value="account">Cuenta</option>
                                <option value="subaccount">Subcuenta</option>
                              </select>
                            </div>
                            <div className="fs-comp-59">
                              <label className="fs-comp-60">Cuenta Padre (se deduce del código si se deja vacío)</label>
                              <select name="newParent" defaultValue="" className="fs-comp-63">
                                <option value="">— Ninguna (cuenta raíz) —</option>
                                {chartOfAccounts.filter(c => c.id).map(c => (
                                  <option key={c.code} value={c.id}>{c.code} — {c.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="fs-comp-79">
                            <button type="submit" disabled={creatingAccount} className="fs-comp-107">
                              {creatingAccount ? 'Creando…' : 'Crear Cuenta Contable'}
                            </button>
                          </div>
                        </form>
                      )}

                      <div className="fs-comp-66">
                        <table className="fs-comp-83">
                          <thead className="fs-comp-68">
                            <tr>
                              <th className="fs-comp-113">Fecha</th>
                              <th className="fs-comp-85">Asiento</th>
                              <th className="fs-comp-69">Concepto</th>
                              <th className="fs-comp-86">Debe</th>
                              <th className="fs-comp-86">Haber</th>
                              <th className="fs-comp-86">Saldo</th>
                            </tr>
                          </thead>
                          <tbody className="fs-comp-71">
                            {journalLoading ? (
                              Array.from({ length: 4 }).map((_, i) => (
                                <tr key={i} className="fs-comp-115">
                                  {Array.from({ length: 6 }).map((_, j) => (
                                    <td key={j} className="fs-comp-116"><Skeleton height="1.2em" /></td>
                                  ))}
                                </tr>
                              ))
                            ) : ledgerRows.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="fs-comp-114">No hay apuntes contables registrados para esta cuenta.</td>
                              </tr>
                            ) : (
                              ledgerRows.map((row, idx) => (
                                <tr key={idx} className="fs-comp-115">
                                  <td className="fs-comp-116">{row.date}</td>
                                  <td className="fs-comp-72">{row.entryId}</td>
                                  <td className="fs-comp-117">{row.concept}</td>
                                  <td className="fs-comp-74">{row.debit > 0 ? formatCurrency(row.debit) : '—'}</td>
                                  <td className="fs-comp-75">{row.credit > 0 ? formatCurrency(row.credit) : '—'}</td>
                                  <td className="fs-comp-118">{formatCurrency(row.runningBalance)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ========================================================= */}
            {/* 6. DIARIO DE DOCUMENTOS (Chronological trace by document) */}
            {/* ========================================================= */}
            {activeMenu === 'diario_docs' && (
              <div className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Visualización del Diario de Operaciones Contables agrupado por tipología documental que respalda cada apunte, garantizando la trazabilidad obligatoria por el <span className="fs-comp-24">Código de Comercio español</span>.
                  </p>
                </div>

                <div className="fs-comp-26">
                  {['Factura Emitida', 'Factura Recibida', 'Arrastre de Saldos', 'Bancos para conciliación', 'Otros'].map(docType => {
                    const matching = entries.filter(e => {
                      if (docType === 'Otros') return !e.sourceModule || !['Factura Emitida', 'Factura Recibida', 'Arrastre de Saldos', 'Bancos para conciliación'].includes(e.sourceModule);
                      return e.sourceModule === docType;
                    });

                    return (
                      <div key={docType} className="fs-comp-93">
                        <div className="fs-comp-119">
                          <span>Tipo Documental: {docType}</span>
                          <span className="fs-comp-95">{matching.length} ASIENTOS</span>
                        </div>
                        {journalLoading ? (
                          <div className="fs-comp-44">
                            {Array.from({ length: 2 }).map((_, i) => (
                              <div key={i} className="fs-comp-121"><Skeleton height="1.2em" /></div>
                            ))}
                          </div>
                        ) : matching.length === 0 ? (
                          <div className="fs-comp-120">Sin movimientos asociados registrados.</div>
                        ) : (
                          <div className="fs-comp-44">
                            {matching.map(e => (
                              <div key={e.id} className="fs-comp-121">
                                <div className="fs-comp-9">
                                  <div className="fs-comp-14">
                                    <span className="fs-comp-122">{e.id}</span>
                                    <span className="fs-comp-123">{e.concept}</span>
                                  </div>
                                  <span className="fs-comp-124">FECHA DE REGISTRO: {e.date} • {e.lines.length} APUNTES</span>
                                </div>
                                <button 
                                  onClick={() => { setActiveMenu('review_entries'); }}
                                  className="fs-comp-125"
                                >
                                  Ver Detalles
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* 7. DIARIO (Create Manual Journal Entry) */}
            {/* ========================================================= */}
            {activeMenu === 'diario' && (
              <form onSubmit={async (e: any) => {
                e.preventDefault();
                const concept = e.target.concept.value;
                const d1 = parseFloat(e.target.d1.value) || 0;
                const c1 = parseFloat(e.target.c1.value) || 0;
                const d2 = parseFloat(e.target.d2.value) || 0;
                const c2 = parseFloat(e.target.c2.value) || 0;

                const lines: JournalLine[] = [
                  { 
                    account: e.target.acc1.value, 
                    accountName: chartOfAccounts.find(c => c.code === e.target.acc1.value)?.name || '', 
                    debit: d1, 
                    credit: c1 
                  },
                  { 
                    account: e.target.acc2.value, 
                    accountName: chartOfAccounts.find(c => c.code === e.target.acc2.value)?.name || '', 
                    debit: d2, 
                    credit: c2 
                  }
                ];

                const ok = await addJournalEntry(concept, lines, 'Diario Manual');
                if (ok) setActiveMenu('review_entries');
              }} className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Formulario para registrar un asiento de diario directo de doble entrada. Asegúrese de que el Debe y el Haber de sus registros sumen el mismo importe neto para que la contabilidad cuadre.
                  </p>
                </div>

                <div className="fs-comp-126">
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Concepto de Asiento Contable</label>
                    <input name="concept" required placeholder="EJ: REGULARIZACIÓN O AJUSTE MANUAL EXTRAORDINARIO" type="text" className="fs-comp-127" />
                  </div>

                  {/* Two Double-Entry Lines for manual entry */}
                  <div className="fs-comp-128">
                    <span className="fs-comp-129">Apuntes de Cuentas Contables</span>
                    
                    {/* Line 1 */}
                    <div className="fs-comp-130">
                      <div className="fs-comp-7">
                        <label className="fs-comp-131">Cuenta PGC 1</label>
                        <select name="acc1" className="fs-comp-132">
                          {postableAccounts.map(c => <option key={c.code} value={c.code}>{c.code} - {c.name}</option>)}
                        </select>
                      </div>
                      <div className="fs-comp-7">
                        <label className="fs-comp-131">Debe (€)</label>
                        <input name="d1" type="number" step="0.01" defaultValue="100.00" className="fs-comp-132" />
                      </div>
                      <div className="fs-comp-7">
                        <label className="fs-comp-131">Haber (€)</label>
                        <input name="c1" type="number" step="0.01" defaultValue="0.00" className="fs-comp-132" />
                      </div>
                    </div>

                    {/* Line 2 */}
                    <div className="fs-comp-130">
                      <div className="fs-comp-7">
                        <label className="fs-comp-131">Cuenta PGC 2</label>
                        <select name="acc2" defaultValue="572000" className="fs-comp-132">
                          {postableAccounts.map(c => <option key={c.code} value={c.code}>{c.code} - {c.name}</option>)}
                        </select>
                      </div>
                      <div className="fs-comp-7">
                        <label className="fs-comp-131">Debe (€)</label>
                        <input name="d2" type="number" step="0.01" defaultValue="0.00" className="fs-comp-132" />
                      </div>
                      <div className="fs-comp-7">
                        <label className="fs-comp-131">Haber (€)</label>
                        <input name="c2" type="number" step="0.01" defaultValue="100.00" className="fs-comp-132" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="fs-comp-79">
                  <button type="submit" className="fs-comp-107">
                    Contabilizar Asiento Contable Manual
                  </button>
                </div>
              </form>
            )}

            {/* ========================================================= */}
            {/* 8. BANCOS PARA CONCILIACIÓN (Bank feed reconciliation) */}
            {/* ========================================================= */}
            {activeMenu === 'bancos' && (
              <div className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Conciliación bancaria: cada ingreso se cuadra con una factura emitida pendiente de cobro (se propone la de importe exacto) y cada gasto se asienta contra la cuenta que elijas. Al confirmar se contabiliza el asiento y se marca el movimiento como conciliado.
                  </p>
                </div>

                <div className="fs-comp-128">
                  <span className="fs-comp-65">Movimientos Bancarios Pendientes de Conciliación</span>
                  
                  <div className="fs-comp-133">
                    {!bankLoading && (() => {
                      const pendingProposals = unreconciledMovements.filter((m: any) =>
                        bankProposals[m.id] && bankMatch[m.id] === bankProposals[m.id].invoiceId && !bankPosted[m.id]).length;
                      return pendingProposals > 0 ? (
                        <div className="fs-comp-134">
                          <div className="fs-comp-7">
                            <p className="fs-comp-109">{pendingProposals} propuesta(s) automática(s) pendiente(s) de validar</p>
                          </div>
                          <div className="fs-comp-136">
                            <button disabled={bankBatchRunning || bankBusyId !== null} onClick={handleConfirmProposals} className="fs-comp-137">
                              {bankBatchRunning ? 'Conciliando…' : `Confirmar ${pendingProposals} propuesta(s)`}
                            </button>
                          </div>
                        </div>
                      ) : null;
                    })()}
                    {bankLoading && renderBankMovementsSkeleton()}
                    {!bankLoading && (() => {
                      const withProposal = unreconciledMovements.filter((m: any) => bankProposals[m.id]);
                      const exceptions = unreconciledMovements.filter((m: any) => !bankProposals[m.id]);
                      const counts: Record<string, number> = {};
                      exceptions.forEach((m: any) => { const k = bankClassify(m).label; counts[k] = (counts[k] || 0) + 1; });
                      return (
                        <>
                          {withProposal.length > 0 && (
                            <p className="fs-comp-109" style={{ marginTop: 8 }}>PROPUESTAS PENDIENTES DE VALIDAR ({withProposal.length})</p>
                          )}
                          {withProposal.map((btx: any) => renderBankCard(btx))}
                          {exceptions.length > 0 && (
                            <p className="fs-comp-109" style={{ marginTop: 12 }}>
                              BANDEJA DE EXCEPCIONES ({exceptions.length}) — sin pareja automática:{' '}
                              {Object.keys(counts).map(k => `${counts[k]} ${k.toLowerCase()}`).join(' · ')}
                            </p>
                          )}
                          {exceptions.map((btx: any) => renderBankCard(btx))}
                        </>
                      );
                    })()}
                    {!bankLoading && unreconciledMovements.length === 0 && (
                      <div className="fs-comp-120">Todas las transacciones bancarias están conciliadas y al día.</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* 9. CONTABILIZAR FACTURA RECIBIDA (Supplier Purchases) */}
            {/* ========================================================= */}
            {activeMenu === 'factura_recibida' && (
              <form onSubmit={async (e: any) => {
                e.preventDefault();
                const provider = e.target.provider.value;
                const docNum = e.target.docNum.value;
                const base = parseFloat(e.target.base.value) || 0;
                const taxRate = parseFloat(e.target.taxRate.value) || 0;
                const irpfRate = parseFloat(e.target.irpfRate.value) || 0;
                
                const iva = base * (taxRate / 100);
                const irpf = base * (irpfRate / 100);
                const total = base + iva - irpf;

                const lines: JournalLine[] = [
                  { account: e.target.expenseAcc.value, accountName: chartOfAccounts.find(c => c.code === e.target.expenseAcc.value)?.name || '', debit: base, credit: 0 },
                  { account: '472000', accountName: 'H.P. IVA Soportado', debit: iva, credit: 0 }
                ];

                if (irpf > 0) {
                  lines.push({ account: '475100', accountName: 'H.P. Acreedora por Retenciones (IRPF)', debit: 0, credit: irpf });
                }

                lines.push({ account: '400000', accountName: 'Proveedores (Euros)', debit: 0, credit: total });

                const ok = await addJournalEntry(`Factura Recibida ${docNum} de ${provider}`, lines, 'Factura Recibida');
                if (ok) setActiveMenu('review_entries');
              }} className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Introduzca los datos de la factura recibida del proveedor o acreedor. El sistema calculará el desglose fiscal de <span className="fs-comp-24">IVA Soportado</span> y retenciones de <span className="fs-comp-24">IRPF</span> correspondientes para generar el asiento contable.
                  </p>
                </div>

                <div className="fs-comp-58">
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Acreedor (CIF / Razón Social)</label>
                    <input name="provider" required placeholder="EJ: PAPELERA CENTRAL S.A. A-88123123" type="text" className="fs-comp-127" />
                  </div>
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Número de Factura de Origen</label>
                    <input name="docNum" required placeholder="EJ: FACT-2026/092" type="text" className="fs-comp-127" />
                  </div>
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Base Imponible (€)</label>
                    <input name="base" required step="0.01" min="0.01" defaultValue="1000.00" type="number" className="fs-comp-63" />
                  </div>

                  <div className="fs-comp-138">
                    <div className="fs-comp-59">
                      <label className="fs-comp-60">Tipo IVA (%)</label>
                      <select name="taxRate" className="fs-comp-63">
                        <option value="21">Estándar (21%)</option>
                        <option value="10">Reducido Editorial (10%)</option>
                        <option value="4">Súper Reducido (4%)</option>
                        <option value="0">Exento (0%)</option>
                      </select>
                    </div>
                    <div className="fs-comp-59">
                      <label className="fs-comp-60">Retención IRPF (%)</label>
                      <select name="irpfRate" className="fs-comp-63">
                        <option value="0">Sin Retención (0%)</option>
                        <option value="15">Profesionales (15%)</option>
                        <option value="19">Alquileres (19%)</option>
                      </select>
                    </div>
                  </div>

                  <div className="fs-comp-62">
                    <label className="fs-comp-60">Cuenta de Gasto Contable Destinataria (PGC)</label>
                    <select name="expenseAcc" className="fs-comp-63">
                      <option value="600000">600000 — Compras de mercaderías (Materia prima, papel, libros)</option>
                      <option value="628000">628000 — Suministros (Agua, luz, internet, electricidad)</option>
                      <option value="629000">629000 — Otros servicios (Gastos de gestión diversos)</option>
                    </select>
                  </div>
                </div>

                <div className="fs-comp-79">
                  <button type="submit" className="fs-comp-107">
                    Contabilizar Factura Recibida y Generar Asiento
                  </button>
                </div>
              </form>
            )}

            {/* ========================================================= */}
            {/* 10. CONTABILIZAR FACTURA EMITIDA (Client Sales) */}
            {/* ========================================================= */}
            {activeMenu === 'factura_emitida' && (
              <form onSubmit={async (e: any) => {
                e.preventDefault();
                const clientId = e.target.clientId.value;
                const client = clientsList.find(c => c.id === clientId)?.name || clientId;
                const docNum = e.target.docNum.value;
                const base = parseFloat(e.target.base.value) || 0;
                const taxRate = parseFloat(e.target.taxRate.value) || 0;

                const iva = base * (taxRate / 100);
                const total = base + iva;

                if (!clientId) {
                  showFeedback('Debe seleccionar un cliente real registrado en el sistema.', true);
                  return;
                }

                const token = getAccessToken();
                if (token) {
                  try {
                    const createUrl = `/invoices_create?access_token=${encodeURIComponent(token)}&company_id=${encodeURIComponent(getActiveCompanyId())}&client_id=${encodeURIComponent(clientId)}&invoice_number=${encodeURIComponent(docNum)}&subtotal=${encodeURIComponent(base.toFixed(2))}&tax_amount=${encodeURIComponent(iva.toFixed(2))}&total=${encodeURIComponent(total.toFixed(2))}&currency=EUR&status=draft`;
                    let createRes = await ObexRequestsHandlerP80.get(createUrl);
                    if (createRes?.data) createRes = createRes.data;
                    if (createRes?.result) createRes = createRes.result;
                    const invId = createRes?.response?.id || createRes?.id || (Array.isArray(createRes?.response) ? createRes.response[0]?.id : null);
                    if (invId) {
                      const addLineUrl = `/invoice_lines_add?access_token=${encodeURIComponent(token)}&invoice_id=${encodeURIComponent(String(invId))}&description=${encodeURIComponent(`Venta a ${client}`)}&quantity=1&unit_price=${encodeURIComponent(base.toFixed(2))}&discount_pct=0&tax_rate=${encodeURIComponent(String(taxRate))}&line_total=${encodeURIComponent(base.toFixed(2))}`;
                      await ObexRequestsHandlerP80.get(addLineUrl);

                      const issueUrl = `/invoices_issue?access_token=${encodeURIComponent(token)}&id=${encodeURIComponent(String(invId))}`;
                      await ObexRequestsHandlerP80.get(issueUrl);
                    } else {
                      showFeedback('Aviso: la factura no se pudo registrar en el sistema de facturación (el backend no devolvió un id). Se registrará igualmente el asiento contable en el Libro Diario.', true);
                    }
                  } catch (err) {
                    console.error('Error creating invoice via backend API:', err);
                  }
                }

                const lines: JournalLine[] = [
                  { account: '430000', accountName: 'Clientes (Euros)', debit: total, credit: 0 },
                  { account: e.target.revenueAcc.value, accountName: chartOfAccounts.find(c => c.code === e.target.revenueAcc.value)?.name || '', debit: 0, credit: base },
                  { account: '477000', accountName: 'H.P. IVA Repercutido', debit: 0, credit: iva }
                ];

                const ok = await addJournalEntry(`Factura Emitida ${docNum} a ${client}`, lines, 'Factura Emitida');
                if (ok) {
                  showFeedback(`✓ Factura ${docNum} registrada en base de datos y contabilizada en el Libro Diario.`);
                  setActiveMenu('review_entries');
                }
              }} className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Contabilice las ventas y facturas emitidas a clientes. El sistema aplicará y desglosará fiscalmente el <span className="fs-comp-24">IVA Repercutido</span>, liquidando la base imponible en cuentas del Grupo 7 (Ventas).
                  </p>
                </div>

                <div className="fs-comp-58">
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Cliente</label>
                    <select name="clientId" required defaultValue="" className="fs-comp-63">
                      <option value="" disabled>— Seleccione un cliente registrado —</option>
                      {clientsList.map(c => (
                        <option key={c.id} value={c.id}>{c.name}{c.cif ? ` — ${c.cif}` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Número de Factura Emitida</label>
                    <input name="docNum" required placeholder="EJ: ALBATROSS-2026/001" type="text" className="fs-comp-127" />
                  </div>
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Base Imponible de la Venta (€)</label>
                    <input name="base" required step="0.01" min="0.01" defaultValue="1500.00" type="number" className="fs-comp-63" />
                  </div>
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Tipo de IVA aplicable (%)</label>
                    <select name="taxRate" className="fs-comp-63">
                      <option value="21">Estándar General (21%)</option>
                      <option value="10">Reducido Editores (10%)</option>
                      <option value="4">Súper Reducido (4%)</option>
                    </select>
                  </div>
                  <div className="fs-comp-62">
                    <label className="fs-comp-60">Cuenta de Ingreso Contable (PGC)</label>
                    <select name="revenueAcc" className="fs-comp-63">
                      <option value="700000">700000 — Ventas de mercaderías (Libros y producciones)</option>
                      <option value="705000">705000 — Prestación de servicios (Distribución o asesoría)</option>
                    </select>
                  </div>
                </div>

                <div className="fs-comp-79">
                  <button type="submit" className="fs-comp-107">
                    Contabilizar Factura Emitida y Generar Asiento
                  </button>
                </div>
              </form>
            )}

            {/* ========================================================= */}
            {/* 10b. HISTORIAL DE FACTURAS (invoices_list + invoices_get) */}
            {/* ========================================================= */}
            {activeMenu === 'facturas_historial' && (
              <div className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Consulta de todas las facturas emitidas registradas en el sistema. Pulse sobre una factura para ver su desglose de líneas.
                  </p>
                </div>

                <div className="fs-comp-26">
                  {invoicesHistoryLoading ? (
                    renderEntriesSkeleton()
                  ) : invoicesHistoryUnavailable ? (
                    <div className="fs-comp-120" style={{ color: '#f87171' }}>API NO_DISPONIBLE — no se pudo conectar con /invoices_list</div>
                  ) : invoicesHistory.length === 0 ? (
                    <div className="fs-comp-120">No hay facturas registradas.</div>
                  ) : (
                    invoicesHistory.map((inv) => {
                      const isExpanded = selectedInvoiceId === String(inv.id);
                      const invId = String(inv.id);
                      const clientName = clientsList.find(c => c.id === String(inv.client_id))?.name || `Cliente #${inv.client_id}`;
                      const isBusy = invoiceActionLoading === invId;
                      const isDraft = inv.status === 'draft';
                      const isTerminal = inv.status === 'collected' || inv.status === 'collected_negotiated';
                      return (
                        <div key={inv.id} className="fs-comp-27">
                          <div
                            className="fs-comp-28"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              if (isExpanded) { setSelectedInvoiceId(null); setSelectedInvoiceDetail(null); return; }
                              fetchInvoiceDetail(invId);
                            }}
                          >
                            <div className="fs-comp-29">
                              <span className="fs-comp-25">{inv.invoice_number || `#${inv.id}`}</span>
                              <span className="fs-comp-30">{inv.created_at ? inv.created_at.split('T')[0] : ''}</span>
                              <span className="fs-comp-31">{clientName}</span>
                            </div>
                            <div className="fs-comp-29">
                              <span className="fs-comp-32">{(INVOICE_STATUS_LABELS[inv.status] || inv.status || '').toUpperCase()}</span>
                              <span className="fs-comp-25">{formatCurrency(parseFloat(inv.total) || 0)}</span>
                              {!isTerminal && (
                                <div className="fs-comp-33" onClick={(e) => e.stopPropagation()}>
                                  {isDraft && (
                                    <>
                                      <button disabled={isBusy} onClick={() => openEditInvoice(inv)} className="fs-comp-34" title="Editar cabecera de la factura">
                                        <Edit3 size={11} /> Editar
                                      </button>
                                      <button disabled={isBusy} onClick={() => handleIssueInvoice(invId)} className="fs-comp-36" title="Emitir factura">
                                        <FileOutput size={11} /> Emitir
                                      </button>
                                      <button disabled={isBusy} onClick={() => setVoidModalInvoice({ id: invId, number: inv.invoice_number || `#${inv.id}` })} className="fs-comp-35" title="Anular factura (solo borradores)">
                                        <Trash2 size={11} /> Anular
                                      </button>
                                    </>
                                  )}
                                  {!isDraft && (
                                    <>
                                      <button disabled={isBusy} onClick={() => handleCollectInvoice(invId)} className="fs-comp-36" title="Marcar como cobrada">
                                        <CheckCircle2 size={11} /> Cobrar
                                      </button>
                                      <button
                                        disabled={isBusy}
                                        onClick={() => {
                                          const currentTotal = parseFloat(inv.total) || 0;
                                          setNegotiateModalInvoice({ id: invId, number: inv.invoice_number || `#${inv.id}`, total: currentTotal });
                                          setNegotiateAmount(String(currentTotal));
                                          setNegotiateTerms('');
                                        }}
                                        className="fs-comp-34"
                                        title="Renegociar importe"
                                      >
                                        <Edit3 size={11} /> Negociar
                                      </button>
                                      {inv.status !== 'executive' && (
                                        <button disabled={isBusy} onClick={() => handleSetExecutiveInvoice(invId)} className="fs-comp-35" title="Pasar a vía ejecutiva">
                                          <ShieldAlert size={11} /> Vía Ejecutiva
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="fs-comp-38">
                              {invoiceDetailLoading ? (
                                <table className="fs-comp-39">
                                  <thead>
                                    <tr className="fs-comp-40">
                                      <th className="fs-comp-41">Descripción</th>
                                      <th className="fs-comp-43">Cantidad</th>
                                      <th className="fs-comp-43">Precio Ud.</th>
                                      <th className="fs-comp-43">IVA</th>
                                      <th className="fs-comp-43">Total Línea</th>
                                    </tr>
                                  </thead>
                                  <tbody className="fs-comp-44">
                                    {Array.from({ length: 3 }).map((_, i) => (
                                      <tr key={i} className="fs-comp-45">
                                        <td className="fs-comp-46"><Skeleton height="1.2em" /></td>
                                        <td className="fs-comp-48"><Skeleton height="1.2em" /></td>
                                        <td className="fs-comp-48"><Skeleton height="1.2em" /></td>
                                        <td className="fs-comp-48"><Skeleton height="1.2em" /></td>
                                        <td className="fs-comp-48"><Skeleton height="1.2em" /></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : !selectedInvoiceDetail ? (
                                <div className="fs-comp-120" style={{ color: '#f87171' }}>No se pudo cargar el detalle de esta factura.</div>
                              ) : (
                                <>
                                  <table className="fs-comp-39">
                                    <thead>
                                      <tr className="fs-comp-40">
                                        <th className="fs-comp-41">Descripción</th>
                                        <th className="fs-comp-43">Cantidad</th>
                                        <th className="fs-comp-43">Precio Ud.</th>
                                        <th className="fs-comp-43">IVA</th>
                                        <th className="fs-comp-43">Total Línea</th>
                                        {isDraft && <th className="fs-comp-43">Acciones</th>}
                                      </tr>
                                    </thead>
                                    <tbody className="fs-comp-44">
                                      {(selectedInvoiceDetail.lines || []).length === 0 ? (
                                        <tr className="fs-comp-45">
                                          <td className="fs-comp-46" colSpan={isDraft ? 6 : 5}>Esta factura no tiene líneas registradas.</td>
                                        </tr>
                                      ) : (
                                        selectedInvoiceDetail.lines.map((line: any, idx: number) => (
                                          <tr key={idx} className="fs-comp-45">
                                            <td className="fs-comp-46">{line.description}</td>
                                            <td className="fs-comp-48">{line.quantity}</td>
                                            <td className="fs-comp-48">{formatCurrency(parseFloat(line.unit_price) || 0)}</td>
                                            <td className="fs-comp-48">{line.tax_rate}%</td>
                                            <td className="fs-comp-49">{formatCurrency(parseFloat(line.line_total) || 0)}</td>
                                            {isDraft && (
                                              <td className="fs-comp-48">
                                                <div className="fs-comp-33" style={{ justifyContent: 'flex-end' }}>
                                                  <button
                                                    type="button"
                                                    disabled={isBusy}
                                                    onClick={() => openEditLine(invId, inv.invoice_number || `#${inv.id}`, line)}
                                                    className="fs-comp-34"
                                                    title="Editar línea"
                                                  >
                                                    <Edit3 size={11} /> Editar
                                                  </button>
                                                  <button
                                                    type="button"
                                                    disabled={isBusy || selectedInvoiceDetail.lines.length <= 1}
                                                    onClick={() => setRemoveLineModal({ invoiceId: invId, lineId: String(line.id), description: line.description || 'esta línea' })}
                                                    className="fs-comp-35"
                                                    style={{ opacity: selectedInvoiceDetail.lines.length <= 1 ? 0.4 : 1 }}
                                                    title={selectedInvoiceDetail.lines.length <= 1 ? 'Una factura no puede quedarse sin líneas' : 'Eliminar línea'}
                                                  >
                                                    <Trash2 size={11} /> Borrar
                                                  </button>
                                                </div>
                                              </td>
                                            )}
                                          </tr>
                                        ))
                                      )}
                                    </tbody>
                                  </table>
                                  {(selectedInvoiceDetail.client_email || selectedInvoiceDetail.client_cif) && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                                      <p className="fs-comp-57" style={{ margin: 0 }}>
                                        {selectedInvoiceDetail.client_name} — CIF {selectedInvoiceDetail.client_cif} — {selectedInvoiceDetail.client_email || '(sin email)'}
                                      </p>
                                      <button
                                        type="button"
                                        disabled={isBusy || invoiceEmailStatus === 'sending'}
                                        onClick={() => handleSendInvoiceEmail(selectedInvoiceId, selectedInvoiceDetail)}
                                        className="fs-comp-34"
                                        style={{ padding: '0.375rem 0.75rem', fontSize: '0.625rem' }}
                                      >
                                        {invoiceEmailStatus === 'sending' ? (
                                          <>
                                            <span style={{
                                              display: 'inline-block',
                                              width: '10px',
                                              height: '10px',
                                              border: '2px solid currentColor',
                                              borderRightColor: 'transparent',
                                              borderRadius: '50%',
                                              animation: 'spin 1s linear infinite',
                                              marginRight: '0.375rem'
                                            }} />
                                            Enviando...
                                          </>
                                        ) : invoiceEmailStatus === 'success' ? (
                                          <>
                                            <Check size={11} style={{ marginRight: '0.375rem' }} />
                                            Enviado
                                          </>
                                        ) : (
                                          <>
                                            <Mail size={11} style={{ marginRight: '0.375rem' }} />
                                            Enviar por email
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* 11. CIERRE DE PERIODO FINANCIERO */}
            {/* ========================================================= */}
            {activeMenu === 'cierre' && (
              <div className="fs-comp-21">
                <div className="fs-comp-56">
                  <h4 className="fs-comp-31">Proceso de Bloqueo y Cierre de Ejercicio Contable</h4>
                  <p className="fs-comp-57">
                    El cierre de periodo contable bloquea permanentemente todas las transacciones dentro de las fechas fijadas y genera el <span className="fs-comp-25">Asiento de Regularización Contable</span> de las cuentas anuales.
                  </p>
                </div>

                <div className="fs-comp-58">
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Periodo a Bloquear</label>
                    <select className="fs-comp-63" value={closePeriod} onChange={(e) => setClosePeriod(e.target.value)}>
                      <option>Primer Trimestre 2026</option>
                      <option>Segundo Trimestre 2026</option>
                      <option>Ejercicio Económico Completo 2026</option>
                    </select>
                  </div>
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Fecha Efectiva del Cierre</label>
                    <input type="date" defaultValue="2026-12-31" className="fs-comp-63" />
                  </div>
                </div>

                <div className="fs-comp-139">
                  <ShieldAlert size={18} className="fs-comp-140" />
                  <div className="fs-comp-7">
                    <span className="fs-comp-141">ADVERTENCIA LEGAL CONTABLE</span>
                    <p className="fs-comp-142">
                      Al ejecutar el cierre contable, todos los asientos del diario para este periodo serán firmados y bloqueados de forma que no se puedan volver a editar o alterar, garantizando la inviolabilidad contable de cara a futuras auditorías o inspecciones de la AEAT.
                    </p>
                  </div>
                </div>

                <div className="fs-comp-79">
                  <button 
                    onClick={() => {
                      showFeedback("Periodo contable cerrado correctamente. Generado asiento de regularización de cierre de cuentas anuales.");
                      setActiveMenu('review_entries');
                    }}
                    type="button" 
                    className="fs-comp-143"
                  >
                    Cerrar Periodo y Bloquear Libro Diario
                  </button>
                </div>

                <FinancePostingPeriodsView period={closePeriod} />
              </div>
            )}

            {/* ========================================================= */}
            {/* 12. RECIBOS (Fast Cash entries) */}
            {/* ========================================================= */}
            {activeMenu === 'recibos' && (
              <form onSubmit={async (e: any) => {
                e.preventDefault();
                const concept = e.target.concept.value;
                const total = parseFloat(e.target.total.value) || 0;
                
                // Simplified cash receipt entry calculations (21% default included)
                const base = total / 1.21;
                const iva = total - base;

                const lines: JournalLine[] = [
                  { account: '629000', accountName: 'Otros servicios (Gastos diversos)', debit: base, credit: 0 },
                  { account: '472000', accountName: 'H.P. IVA Soportado', debit: iva, credit: 0 },
                  { account: '570000', accountName: 'Caja, euros', debit: 0, credit: total }
                ];

                const ok = await addJournalEntry(`Recibo de Caja: ${concept}`, lines, 'Recibos');
                if (ok) setActiveMenu('review_entries');
              }} className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Formulario rápido de caja (cuenta 570) para registrar tickets o facturas simplificadas abonadas al contado (ej: gastos de material de oficina o combustible).
                  </p>
                </div>

                <div className="fs-comp-58">
                  <div className="fs-comp-62">
                    <label className="fs-comp-60">Concepto o Detalle del Ticket</label>
                    <input name="concept" required placeholder="EJ: GASOLINA MENSAJERO / COMPRA MATERIAL DE EMBALAJE" type="text" className="fs-comp-127" />
                  </div>
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Importe Total del Recibo (€ - IVA incluido)</label>
                    <input name="total" required step="0.01" min="0.01" defaultValue="45.00" type="number" className="fs-comp-63" />
                  </div>
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Método de Descargo de Caja</label>
                    <select className="fs-comp-63">
                      <option>Caja Chica Chica (570000)</option>
                      <option>Fondo de Caja Central (570001)</option>
                    </select>
                  </div>
                </div>

                <div className="fs-comp-79">
                  <button type="submit" className="fs-comp-107">
                    Contabilizar Ticket / Recibo de Caja
                  </button>
                </div>
              </form>
            )}

            {/* ========================================================= */}
            {/* 13. IMPUESTOS (Official Tax forms calculation) */}
            {/* ========================================================= */}
            {activeMenu === 'impuestos' && (
              <div className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Este panel liquida y calcula los importes acumulados de las declaraciones fiscales obligatorias de la Hacienda Pública (AEAT).
                  </p>
                </div>

                <div className="fs-comp-144" style={taxModel ? { gridTemplateColumns: '1fr' } : undefined}>
                  
                  {/* Modelo 303 */}
                  {taxModel !== '111' && (
                  <div className="fs-comp-145">
                    <div className="fs-comp-146">
                      <span className="fs-comp-147">Modelo 303 (Trimestral IVA)</span>
                      <span className="fs-comp-148">Trimestre actual</span>
                    </div>

                    {(() => {
                      let ivaSoportado = 0;
                      let ivaRepercutido = 0;

                      entries.forEach(e => {
                        e.lines.forEach(l => {
                          if (accountMatches(l.account, '472000')) ivaSoportado += l.debit;
                          if (accountMatches(l.account, '477000')) ivaRepercutido += l.credit;
                        });
                      });

                      const resultadoLiquidar = ivaRepercutido - ivaSoportado;

                      return (
                        <div className="fs-comp-149">
                          <div className="fs-comp-150">
                            <span className="fs-comp-151">Total IVA Repercutido (Ventas):</span>
                            <span className="fs-comp-24">{formatCurrency(ivaRepercutido)}</span>
                          </div>
                          <div className="fs-comp-150">
                            <span className="fs-comp-151">Total IVA Soportado (Compras):</span>
                            <span className="fs-comp-24">{formatCurrency(ivaSoportado)}</span>
                          </div>
                          <div className="fs-comp-152">
                            <span className="fs-comp-153">Resultado de Liquidación (AEAT):</span>
                            <span className={resultadoLiquidar >= 0 ? "text-amber-500" : "text-emerald-400"}>
                              {resultadoLiquidar >= 0 ? `A PAGAR: ${formatCurrency(resultadoLiquidar)}` : `A COMPENSAR: ${formatCurrency(Math.abs(resultadoLiquidar))}`}
                            </span>
                          </div>

                          <button 
                            onClick={async () => {
                              // Contabilizar liquidacion de IVA
                              const lines: JournalLine[] = [
                                { account: '477000', accountName: 'H.P. IVA Repercutido', debit: ivaRepercutido, credit: 0 },
                                { account: '472000', accountName: 'H.P. IVA Soportado', debit: 0, credit: ivaSoportado }
                              ];
                              if (resultadoLiquidar >= 0) {
                                lines.push({ account: '475000', accountName: 'H.P. Acreedora por IVA', debit: 0, credit: resultadoLiquidar });
                              } else {
                                lines.push({ account: '129000', accountName: 'Resultado del Ejercicio', debit: Math.abs(resultadoLiquidar), credit: 0 }); // Simplification
                              }
                              const ok = await addJournalEntry("Liquidación trimestral de IVA - Modelo 303", lines, 'Impuestos');
                              if (ok) setActiveMenu('review_entries');
                            }}
                            className="fs-comp-154"
                          >
                            Contabilizar Liquidación IVA (M-303)
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                  )}

                  {/* Modelo 111 */}
                  {taxModel !== '303' && (
                  <div className="fs-comp-145">
                    <div className="fs-comp-146">
                      <span className="fs-comp-147">Modelo 111 (Retenciones IRPF)</span>
                      <span className="fs-comp-148">Trimestre actual</span>
                    </div>

                    {(() => {
                      let retenciones = 0;
                      // [07] perceptores, [08] base de los gastos con retención y [09] retenciones practicadas
                      let practicadas = 0;
                      let percepciones = 0;
                      const perceptores: string[] = [];
                      entries.forEach(e => {
                        let retencion = 0;
                        e.lines.forEach(l => {
                          if (accountMatches(l.account, '475100')) {
                            retenciones += l.credit - l.debit;
                            retencion += l.credit;
                          }
                        });
                        if (retencion <= 0) return;
                        practicadas += retencion;
                        e.lines.forEach(l => { if (String(l.account).startsWith('6')) percepciones += l.debit; });
                        // "FACTURA RECIBIDA X DE PROVEEDOR"
                        const supplier = e.concept.match(/ DE (.+)$/);
                        const perceptor = (supplier ? supplier[1] : e.concept).trim();
                        if (!perceptores.includes(perceptor)) perceptores.push(perceptor);
                      });

                      return (
                        <div className="fs-comp-149">
                          <div className="fs-comp-150">
                            <span className="fs-comp-151">[07] Nº de perceptores (actividades económicas):</span>
                            <span className="fs-comp-24">{perceptores.length}</span>
                          </div>
                          <div className="fs-comp-150">
                            <span className="fs-comp-151">[08] Importe de las percepciones:</span>
                            <span className="fs-comp-24">{formatCurrency(percepciones)}</span>
                          </div>
                          <div className="fs-comp-150">
                            <span className="fs-comp-151">[09] Retenciones practicadas (475100):</span>
                            <span className="fs-comp-24">{formatCurrency(practicadas)}</span>
                          </div>
                          <div className="fs-comp-152">
                            <span className="fs-comp-153">[30] Total a ingresar en la AEAT:</span>
                            <span className="fs-comp-155">{formatCurrency(retenciones)}</span>
                          </div>

                          <button 
                            onClick={async () => {
                              if (retenciones <= 0) {
                                showFeedback("No hay retenciones de IRPF pendientes de liquidar.", true);
                                return;
                              }
                              const lines: JournalLine[] = [
                                { account: '475100', accountName: 'H.P. Acreedora por Retenciones (IRPF)', debit: retenciones, credit: 0 },
                                { account: '572000', accountName: 'Bancos c/c (WISE EUR)', debit: 0, credit: retenciones }
                              ];
                              const ok = await addJournalEntry("Pago e ingreso retenciones IRPF - Modelo 111", lines, 'Impuestos');
                              if (ok) setActiveMenu('review_entries');
                            }}
                            className="fs-comp-156"
                          >
                            Contabilizar Liquidación IRPF (M-111)
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                  )}

                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* 14. COBROS SEPA (Direct debit remittances) */}
            {/* ========================================================= */}
            {activeMenu === 'sepa' && (
              <form onSubmit={async (e: any) => {
                e.preventDefault();
                const numRemesa = e.target.numRemesa.value;
                const total = parseFloat(e.target.total.value) || 0;

                const lines: JournalLine[] = [
                  { account: '431000', accountName: 'Clientes, efectos comerciales en cartera', debit: total, credit: 0 },
                  { account: '430000', accountName: 'Clientes (Euros)', debit: 0, credit: total }
                ];

                const ok = await addJournalEntry(`Remisión remesa de efectos SEPA: ${numRemesa}`, lines, 'Cobros SEPA');
                if (ok) {
                  // Simulate download SEPA XML file
                  showFeedback(`Cuaderno SEPA Norma 19 XML descargado. Remesa ID: ${numRemesa} por ${total.toFixed(2)}€.`);
                  setActiveMenu('review_entries');
                }
              }} className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Genere el fichero normalizado XML según la normativa bancaria europea SEPA (Norma 19 del Consejo Superior Bancario) para girar cargos recurrentes en lote directamente a los IBAN de sus clientes.
                  </p>
                </div>

                <div className="fs-comp-58">
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Referencia de la Remesa</label>
                    <input name="numRemesa" required defaultValue="SEPA-CSB-19-2026/01" type="text" className="fs-comp-127" />
                  </div>
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Importe Total del Adeudo Remesado (€)</label>
                    <input name="total" required defaultValue="1210.00" type="number" className="fs-comp-63" />
                  </div>
                </div>

                <div className="fs-comp-79">
                  <button type="submit" className="fs-comp-107">
                    Generar Fichero XML SEPA y Contabilizar Efectos
                  </button>
                </div>
              </form>
            )}

            {/* ========================================================= */}
            {/* 15. CRÉDITOS (French loan amortization generator) */}
            {/* ========================================================= */}
            {activeMenu === 'creditos' && (
              <form onSubmit={async (e: any) => {
                e.preventDefault();
                const principal = parseFloat(e.target.principal.value) || 0;
                const cost = parseFloat(e.target.cost.value) || 0;

                const lines: JournalLine[] = [
                  { account: '572000', accountName: 'Bancos c/c (WISE EUR)', debit: principal - cost, credit: 0 },
                  { account: '669000', accountName: 'Otros gastos financieros (Apertura)', debit: cost, credit: 0 },
                  { account: '170000', accountName: 'Deudas a L/P con entidades de crédito', debit: 0, credit: principal }
                ];

                const ok = await addJournalEntry("Concesión de Préstamo bancario amortizable", lines, 'Créditos');
                if (ok) setActiveMenu('review_entries');
              }} className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Registre préstamos, pólizas de crédito o leasing obtenidos de entidades financieras españolas. El sistema dividirá el principal en deudas a Corto (520) y Largo plazo (170).
                  </p>
                </div>

                <div className="fs-comp-58">
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Capital Concedido (Principal Préstamo €)</label>
                    <input name="principal" required defaultValue="25000.00" type="number" className="fs-comp-63" />
                  </div>
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Gastos de Formalización y Apertura bancaria (€)</label>
                    <input name="cost" required defaultValue="250.00" type="number" className="fs-comp-63" />
                  </div>
                </div>

                <div className="fs-comp-79">
                  <button type="submit" className="fs-comp-107">
                    Contabilizar Préstamo e Ingresar Capital
                  </button>
                </div>
              </form>
            )}

            {/* ========================================================= */}
            {/* 16. PROGRAMAS DE PAGO (Norma 34 mass payments) */}
            {/* ========================================================= */}
            {activeMenu === 'pagos' && (
              <form onSubmit={async (e: any) => {
                e.preventDefault();
                const ref = e.target.ref.value;
                const total = parseFloat(e.target.total.value) || 0;

                const lines: JournalLine[] = [
                  { account: '400000', accountName: 'Proveedores (Euros)', debit: total, credit: 0 },
                  { account: '572000', accountName: 'Bancos c/c (WISE EUR)', debit: 0, credit: total }
                ];

                const ok = await addJournalEntry(`Liquidación lote de pagos Norma 34: ${ref}`, lines, 'Programas de Pago');
                if (ok) {
                  showFeedback(`Generado fichero de transferencia Norma 34 por valor de ${total.toFixed(2)}€.`);
                  setActiveMenu('review_entries');
                }
              }} className="fs-comp-21">
                <div className="fs-comp-22">
                  <p className="fs-comp-57">
                    Lote masivo de pagos y transferencias a acreedores y proveedores (Norma 34). Contabiliza el descargo bancario global y genera el fichero XML SEPA de transferencia para su banco.
                  </p>
                </div>

                <div className="fs-comp-58">
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Referencia Remesa de Pagos</label>
                    <input name="ref" required defaultValue="NORMA34-TRANS-2026/02" type="text" className="fs-comp-127" />
                  </div>
                  <div className="fs-comp-59">
                    <label className="fs-comp-60">Suma de Facturas Liquidadas (€)</label>
                    <input name="total" required defaultValue="3450.00" type="number" className="fs-comp-63" />
                  </div>
                </div>

                <div className="fs-comp-79">
                  <button type="submit" className="fs-comp-107">
                    Generar Norma 34 y Contabilizar Lote de Pagos
                  </button>
                </div>
              </form>
            )}

            {/* ========================================================= */}
            {/* 17. ACTIVOS FIJOS (Asset depreciation) */}
            {/* ========================================================= */}
            {/* Sustituye al formulario de alta rápida de amortización anual (cuentas fijas
                681000/281700 sin depender del tipo de activo) por la ficha de consulta real:
                lista de activos + valores/calendario/parámetros por área de valoración. */}
            {/* {activeMenu === 'activos_fijos' && <FixedAssetsView />} */}

          </div>
        </div>

        {/* Workspace Footer status bar */}
        <div className="fs-comp-157">
          <span>ESTADO DEL SISTEMA CONTABLE: <strong className="fs-comp-95">CONECTADO (PGC 2026)</strong></span>
          <span>Nº ASIENTOS CONTABILIZADOS: {!journalRequested ? <strong className="fs-comp-153">—</strong> : journalLoading ? <Skeleton width="28px" height="1.2em" /> : <strong className="fs-comp-153">{entries.length}</strong>}</span>
        </div>

      </div>

      <Modal
        isOpen={!!deleteModalItem}
        type="urgent"
        title="Anular Asiento"
        message={`¿Seguro que desea anular el asiento contable ${deleteModalItem}?`}
        confirmText="Anular"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModalItem(null)}
      />

      <Modal
        isOpen={!!voidModalInvoice}
        type="urgent"
        title="Anular Factura"
        message={`¿Seguro que desea anular la factura ${voidModalInvoice?.number}? Esta acción es irreversible: se eliminará la factura y sus líneas.`}
        confirmText="Anular"
        cancelText="Cancelar"
        onConfirm={confirmVoidInvoice}
        onCancel={() => setVoidModalInvoice(null)}
      />

      <Modal
        isOpen={!!negotiateModalInvoice}
        type="warning"
        title="Renegociar Factura"
        confirmText="Confirmar Negociación"
        cancelText="Cancelar"
        onConfirm={confirmNegotiateInvoice}
        onCancel={() => setNegotiateModalInvoice(null)}
      >
        <div className="fs-comp-58" style={{ marginTop: '0.5rem' }}>
          <div className="fs-comp-59">
            <label className="fs-comp-60">Factura {negotiateModalInvoice?.number} — Importe original: {formatCurrency(negotiateModalInvoice?.total || 0)}</label>
          </div>
          <div className="fs-comp-59">
            <label className="fs-comp-60">Nuevo Importe Negociado (€)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={negotiateAmount}
              onChange={(e) => setNegotiateAmount(e.target.value)}
              placeholder={String(negotiateModalInvoice?.total || 0)}
              className="fs-comp-63"
            />
          </div>
          <div className="fs-comp-59">
            <label className="fs-comp-60">Condiciones del Acuerdo</label>
            <input
              type="text"
              value={negotiateTerms}
              onChange={(e) => setNegotiateTerms(e.target.value)}
              placeholder="EJ: Pago fraccionado en 3 plazos"
              className="fs-comp-127"
            />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!editInvoiceModal}
        type="info"
        title="Editar Factura"
        confirmText="Guardar Cambios"
        cancelText="Cancelar"
        onConfirm={confirmEditInvoice}
        onCancel={() => setEditInvoiceModal(null)}
      >
        <div className="fs-comp-58" style={{ marginTop: '0.5rem' }}>
          <div className="fs-comp-59">
            <label className="fs-comp-60">Factura {editInvoiceModal?.number} — solo editable en estado borrador</label>
          </div>
          <div className="fs-comp-138">
            <div className="fs-comp-59">
              <label className="fs-comp-60">Serie</label>
              <input type="text" value={editInvoiceForm.series} onChange={(e) => { const v = e.target.value; setEditInvoiceForm(f => ({ ...f, series: v })); }} placeholder="EJ: A" className="fs-comp-127" />
            </div>
            <div className="fs-comp-59">
              <label className="fs-comp-60">Número de Factura</label>
              <input type="text" value={editInvoiceForm.invoiceNumber} onChange={(e) => { const v = e.target.value; setEditInvoiceForm(f => ({ ...f, invoiceNumber: v })); }} className="fs-comp-127" />
            </div>
          </div>
          <div className="fs-comp-138">
            <div className="fs-comp-59">
              <label className="fs-comp-60">Fecha de Vencimiento</label>
              <input type="date" value={editInvoiceForm.dueDate} onChange={(e) => { const v = e.target.value; setEditInvoiceForm(f => ({ ...f, dueDate: v })); }} className="fs-comp-63" />
            </div>
            <div className="fs-comp-59">
              <label className="fs-comp-60">Divisa</label>
              <input type="text" value={editInvoiceForm.currency} onChange={(e) => { const v = e.target.value.toUpperCase(); setEditInvoiceForm(f => ({ ...f, currency: v })); }} maxLength={3} className="fs-comp-63" />
            </div>
          </div>
          <div className="fs-comp-138">
            <div className="fs-comp-59">
              <label className="fs-comp-60">Forma de Pago</label>
              <select value={editInvoiceForm.paymentMethod} onChange={(e) => { const v = e.target.value; setEditInvoiceForm(f => ({ ...f, paymentMethod: v })); }} className="fs-comp-63">
                <option value="">— Sin especificar —</option>
                <option value="cash">Efectivo</option>
                <option value="card">Tarjeta</option>
                <option value="transfer">Transferencia</option>
                <option value="direct_debit">Domiciliación</option>
                <option value="on_account">A Cuenta</option>
              </select>
            </div>
            <div className="fs-comp-59">
              <label className="fs-comp-60">Cuenta Bancaria</label>
              <select value={editInvoiceForm.bankAccountId} onChange={(e) => { const v = e.target.value; setEditInvoiceForm(f => ({ ...f, bankAccountId: v })); }} className="fs-comp-63">
                <option value="">— Sin especificar —</option>
                {bankAccountsList.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
          <div className="fs-comp-59">
            <label className="fs-comp-60">Referencia de Pago (opcional)</label>
            <input type="text" value={editInvoiceForm.paymentInstrumentRef} onChange={(e) => { const v = e.target.value; setEditInvoiceForm(f => ({ ...f, paymentInstrumentRef: v })); }} placeholder="EJ: CHQ-100" className="fs-comp-127" />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!editLineModal}
        type="info"
        title="Editar Línea de Factura"
        confirmText="Guardar Línea"
        cancelText="Cancelar"
        onConfirm={confirmEditLine}
        onCancel={() => setEditLineModal(null)}
      >
        <div className="fs-comp-58" style={{ marginTop: '0.5rem' }}>
          <div className="fs-comp-59">
            <label className="fs-comp-60">Factura {editLineModal?.invoiceNumber} — solo editable en estado borrador</label>
          </div>
          <div className="fs-comp-59">
            <label className="fs-comp-60">Descripción</label>
            <input type="text" value={editLineForm.description} onChange={(e) => { const v = e.target.value; setEditLineForm(f => ({ ...f, description: v })); }} placeholder="EJ: Derecho Civil, tomo II" className="fs-comp-127" />
          </div>
          <div className="fs-comp-138">
            <div className="fs-comp-59">
              <label className="fs-comp-60">Cantidad</label>
              <input type="number" step="0.01" min="0.01" value={editLineForm.quantity} onChange={(e) => { const v = e.target.value; setEditLineForm(f => ({ ...f, quantity: v })); }} className="fs-comp-63" />
            </div>
            <div className="fs-comp-59">
              <label className="fs-comp-60">Precio Unidad (€)</label>
              <input type="number" step="0.01" min="0" value={editLineForm.unitPrice} onChange={(e) => { const v = e.target.value; setEditLineForm(f => ({ ...f, unitPrice: v })); }} className="fs-comp-63" />
            </div>
          </div>
          <div className="fs-comp-59">
            <label className="fs-comp-60">IVA (%)</label>
            <input type="number" step="0.01" min="0" value={editLineForm.taxRate} onChange={(e) => { const v = e.target.value; setEditLineForm(f => ({ ...f, taxRate: v })); }} className="fs-comp-63" />
          </div>
          <div className="fs-comp-59">
            <label className="fs-comp-60">
              Nuevo total de línea (sin IVA): {formatCurrency((parseFloat(editLineForm.quantity) || 0) * (parseFloat(editLineForm.unitPrice) || 0) * (1 - (parseFloat(editLineForm.discountPct) || 0) / 100))}
            </label>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!removeLineModal}
        type="urgent"
        title="Eliminar Línea de Factura"
        message={`¿Seguro que desea eliminar la línea "${removeLineModal?.description}"? Se recalcularán la base imponible, el IVA y el total de la factura.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmRemoveLine}
        onCancel={() => setRemoveLineModal(null)}
      />
    </div>
  );
}
